import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { TrackingLink } from "@/components/TrackingLink";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { STAGE_LABEL, STAGE_BADGE, PRODUCTION_SUBSTAGE, PRODUCTION_SUBSTAGE_BADGE, productionSubstage, productionDeadline } from "@/constants/roles";
import { printProductionSheet } from "@/lib/printProduction";
import { ExtensionRequestButton } from "@/components/ProductionExtensionDialog";
import { QcResolveButton } from "@/components/HoldRespondDialog";
import { TransferSendBackButton } from "@/components/TransferSendBackButton";
import { useSearch } from "@/contexts/SearchContext";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import ShipStationDialog from "@/components/ShipStationDialog";
import SelfCheckoutDialog from "@/components/SelfCheckoutDialog";
import ShipFromShopifyDialog from "@/components/ShipFromShopifyDialog";
import ShopifyBulkImportDialog from "@/components/ShopifyBulkImportDialog";
import ManualPreOrderDialog from "@/components/ManualPreOrderDialog";
import DeletePermanentlyDialog from "@/components/DeletePermanentlyDialog";
import { PoSortHeader, sortByPo } from "@/components/PoSortHeader";
import { ColumnSortHeader, useColumnSort, sortByExtractor } from "@/components/ColumnSortHeader";
import { ORDER_COLUMN_EXTRACTORS } from "@/pages/ordersColumns";
import { useT } from "@/i18n/LanguageContext";
import { UsFlag, isUsOrder } from "@/components/UsFlag";
import { CancelStockStopDialog, CancelStopButton, canConfirmCancelStop } from "@/components/CancelStockStopDialog";
import { HoldStopDialog, HoldStopButton, HoldChip, holdStopDue, canAckHoldStop } from "@/components/HoldStopDialog";
import { StockChip, StockInButton, stockInDue, canStockIn } from "@/components/StockInDialog";
import { WAREHOUSE_LABEL, WAREHOUSE_FLAG, FACTORY_SHORT, pickupInfo, pickupPending } from "@/constants/warehouses";
import { PickupBadge } from "@/components/PickupBadge";
import { useLogoLabel, LogoLabelPreview } from "@/components/LogoLabel";
import { displayCode } from "@/constants/coverCodes";
import { IssuePhotos } from "@/components/IssuePhotos";
import { BatchRow, PoChip, CHIP_GRID, useToggleSet, shippingEnteredDay, groupByTracking, BoxGroups, UndoShipmentButton } from "@/components/BatchRow";
import { isPriorityOrder, pinPriority, PRIORITY_ROW_CLS } from "@/lib/priority";
import { PriorityTag } from "@/components/PriorityTag";
import MakeBoxDialog, { defaultEta } from "@/components/MakeBoxDialog";
import { ColorDot } from "@/components/ColorSwatch";
import { PreorderPhotoUploadButton } from "@/components/PreorderPhotoUploadButton";
import { DiscountChip } from "@/components/ShopifyDiscount";
import { effectiveColor, CustomColorNote } from "@/components/CustomColorNote";
import { holdReasonText } from "@/components/holdReason";

// usePersistedSort — mirrors useState but reads/writes to localStorage under
// `storageKey`. Used to make column-header sorts (PO # ascending/descending)
// stick per tab across navigation & reload. Empty / null / undefined values
// are treated as "no sort" and removed from storage.
function usePersistedSort(storageKey) {
  const [value, setValueState] = useState(() => {
    try {
      const v = typeof window !== "undefined" ? window.localStorage.getItem(storageKey) : null;
      return v && v !== "null" ? v : null;
    } catch { return null; }
  });
  // Re-read when the storage key changes (e.g. user navigates between tabs
  // and the component re-uses the same table for a different stage).
  useEffect(() => {
    try {
      const v = typeof window !== "undefined" ? window.localStorage.getItem(storageKey) : null;
      setValueState(v && v !== "null" ? v : null);
    } catch { /* ignore */ }
  }, [storageKey]);
  const setValue = React.useCallback((v) => {
    setValueState(v);
    try {
      if (v) window.localStorage.setItem(storageKey, v);
      else window.localStorage.removeItem(storageKey);
    } catch { /* ignore */ }
  }, [storageKey]);
  return [value, setValue];
}

const BADGE_CLS = {
  neutral: "bg-slate-100 text-slate-700 border-slate-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  critical: "bg-red-50 text-red-700 border-red-200",
};

export default function OrdersList() {
  const { stage } = useParams();
  const { user } = useAuth();
  const { t, lang } = useT();
  const { query: search, setQuery: setSearch, setPlaceholder } = useSearch();
  const isBackOffice = user.role === "back_office" || user.role === "super_admin";
  const isFactoryRole = user.role === "factory_1" || user.role === "factory_2" || user.role === "factory_3";
  const isBoShipping = stage === "shipping" && (user.role === "back_office" || user.role === "super_admin");
  // Factory roles: their "completed" work = packing has received the order (their job done)
  const backendStage = stage === "completed"
    ? (isFactoryRole ? "completed" : "orders_received")
    : isBoShipping ? "bo_shipping" : stage;
  const { data: rawData = [], isLoading } = useQuery({
    queryKey: ["orders", backendStage],
    queryFn: async () => (await api.get("/orders", { params: { status: backendStage } })).data,
  });
  // Cross-stage search: when the user types a query, also fetch orders across
  // ALL stages so they can find any order that's in another tab. Only runs
  // when a query is present (min 2 chars) — never on empty search.
  const q = (search || "").trim();
  const { data: crossStageData = [] } = useQuery({
    queryKey: ["orders-cross-search", q],
    queryFn: async () => (await api.get("/orders", { params: { search: q } })).data,
    enabled: q.length >= 2,
    staleTime: 30_000,
  });

  // Set a stage-aware placeholder for the header search
  useEffect(() => {
    const label = STAGE_LABEL[stage] || stage || "orders";
    setPlaceholder(isBackOffice
      ? `Search across ALL tabs — PO # · Catalog · Codes · VIN · Vehicle · Colour · Rim · Tracking · Customer…`
      : `Search across ALL tabs — PO # · Catalog · Codes · VIN · Vehicle · Colour · Rim · Tracking…`);
  }, [stage, setPlaceholder, isBackOffice]);

  // Client-side filter across every field a user could reasonably want
  // to search on. Matches the backend list (see `/orders?search=`) so the
  // "current tab" rows and the cross-tab hits use the SAME field set —
  // typing a code, tracking # or colour surfaces the same result everywhere.
  const matches = useMemo(() => {
    const qLower = q.toLowerCase();
    return (o) => {
      if (!qLower) return true;
      const bag = [
        o.po_number,
        o.customer_order_number,
        o.shopify_order_number,
        o.catalog_number,
        o.vin,
        o.brand,
        o.model,
        o.year,
        o.trim,
        o.color,
        o.colour,
        o.rim_size,
        o.front_cover_code,
        o.f_clamp_code,
        o.rear_cover_code,
        o.r_clamp_code,
        o.front_cover_code_f2,
        o.f_clamp_code_f2,
        o.rear_cover_code_f2,
        o.r_clamp_code_f2,
        o.shipping_tracking_number,
        o.tracking_number,
        o.assigned_factory,
        o.status,
        o.flag_reason,
        o.na_reason,
        o.notes,
        o.cancel_reason,
        o.customer?.name,
        o.customer?.email,
        o.customer?.phone,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      return bag.some((v) => v.includes(qLower));
    };
  }, [q]);
  const matched = useMemo(() => rawData.filter(matches), [rawData, matches]);
  // Production is split in two sub-tabs: customer "Daily orders" and 📦 "Batch orders" (stock sets).
  const [prodView, setProdViewState] = useState(() => {
    try { return window.localStorage.getItem("orders-prod-view") || "daily"; } catch { return "daily"; }
  });
  const setProdView = (v) => { setProdViewState(v); try { window.localStorage.setItem("orders-prod-view", v); } catch { /* ignore */ } };
  const batchView = stage === "production" && prodView === "batch";
  const prodCounts = useMemo(() => ({ daily: matched.filter((o) => !o.is_stock_unit).length, batch: matched.filter((o) => !!o.is_stock_unit).length }), [matched]);
  const data = useMemo(() => (stage === "production" ? matched.filter((o) => (batchView ? !!o.is_stock_unit : !o.is_stock_unit)) : matched), [matched, stage, batchView]);
  // Per-view PO # sort state (asc / desc / null). Each visible table maintains
  // its own state via <PoSortHeader />; this one drives the main grid.
  // Persisted per-stage so the user's column-sort choice sticks across
  // navigation & reload until they clear it.
  const [poSort, setPoSort] = usePersistedSort(`orders-po-sort:main:${stage}`);
  // Global sort override — user picks from a dropdown near the page title.
  // When set, this overrides both the built-in default sort AND the PO column
  // sort. Options:
  //   po_asc      · Order # ascending (oldest first)
  //   po_desc     · Order # descending (newest first)
  //   photos_asc  · Photos received oldest first
  //   photos_desc · Photos received newest first
  //   null        · Default (per-stage default sort)
  // Persisted per-stage in localStorage so the user's sort choice sticks
  // between navigations until they explicitly reset it back to Default.
  const sortStorageKey = `orders-sort:${stage}`;
  const [globalSort, setGlobalSortState] = useState(() => {
    try {
      const v = typeof window !== "undefined" ? window.localStorage.getItem(sortStorageKey) : null;
      return v || null;
    } catch { return null; }
  });
  useEffect(() => {
    try {
      const v = typeof window !== "undefined" ? window.localStorage.getItem(sortStorageKey) : null;
      setGlobalSortState(v || null);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);
  const setGlobalSort = (v) => {
    setGlobalSortState(v);
    try {
      if (v) window.localStorage.setItem(sortStorageKey, v);
      else window.localStorage.removeItem(sortStorageKey);
    } catch { /* ignore */ }
  };
  // Per-column sort (Feb 28, 2026 rule: every column header in every stage
  // table must be clickable-to-sort). When `columnSort.key` is set, it
  // OVERRIDES the legacy `globalSort` / `poSort` tracks. Rush urgent/top
  // rows still float to the very top regardless of column sort.
  const [columnSort, setColumnSort] = useColumnSort(`main:${stage}`);
  const sortedData = useMemo(() => {
    // Any order with the `top` or `urgent` rush flag ALWAYS floats to the
    // top regardless of the chosen sort. Urgent wins ties over Top.
    // Order of the pinned block: QC hold ▸ defect re-runs ▸ Rush urgent ▸
    // Rush top ▸ Warranty / Reorder (user rule: Rush first, then Warranty/Reorder).
    const rushRank = (o) => {
      if (o.qc_hold && o.status === "production") return 6;
      if (Number(o.defect_count || 0) > 0) return 5;
      if (o.rush_urgent || Number(o.rush_level || 0) >= 3) return 4;
      if (o.rush_top || Number(o.rush_level || 0) >= 2) return 3;
      if (isPriorityOrder(o)) return 2;
      return 0;
    };
    const applySort = (arr) => {
      // Column-based sort wins over legacy sort tracks when active.
      if (columnSort?.key && columnSort?.dir) {
        const extract = ORDER_COLUMN_EXTRACTORS[columnSort.key];
        if (extract) return sortByExtractor(arr, columnSort.dir, extract);
      }
      if (globalSort) {
        const numFromPo = (o) => {
          const s = (o.po_number || "").replace(/[^0-9]/g, "");
          return s ? parseInt(s, 10) : 0;
        };
        if (globalSort === "po_asc") return [...arr].sort((a, b) => numFromPo(a) - numFromPo(b));
        if (globalSort === "po_desc") return [...arr].sort((a, b) => numFromPo(b) - numFromPo(a));
        if (globalSort === "photos_asc" || globalSort === "photos_desc") {
          const hasPhotos = (o) => !!(o.photos_received_at || o.photos_received);
          const withPhotos = arr.filter(hasPhotos);
          const withoutPhotos = arr.filter((o) => !hasPhotos(o));
          withPhotos.sort((a, b) => {
            const av = a.photos_received_at || "";
            const bv = b.photos_received_at || "";
            return globalSort === "photos_asc" ? av.localeCompare(bv) : bv.localeCompare(av);
          });
          withoutPhotos.sort((a, b) => numFromPo(a) - numFromPo(b));
          return [...withPhotos, ...withoutPhotos];
        }
        if (globalSort === "waiting_reply_asc") {
          const isWaiting = (o) => !!o.waiting_reply;
          const waiting = arr.filter(isWaiting);
          const rest = arr.filter((o) => !isWaiting(o));
          waiting.sort((a, b) => (a.waiting_reply_at || "").localeCompare(b.waiting_reply_at || ""));
          rest.sort((a, b) => numFromPo(a) - numFromPo(b));
          return [...waiting, ...rest];
        }
        // Stage sort — groups rows by their pipeline status (lead ▸ pre_order
        // ▸ pending ▸ advance ▸ production ▸ inventory_packing ▸ packing ▸
        // shipping ▸ completed ▸ na ▸ flagged ▸ cancelled). Useful on tabs
        // that mix statuses (e.g. Pre Orders shows both pre_order + lead).
        if (globalSort === "stage_asc" || globalSort === "stage_desc") {
          const STAGE_ORDER = ["lead", "pre_order", "pending", "advance",
            "production", "product_transfer", "inventory_packing",
            "packing", "shipping", "completed", "na", "flagged", "cancelled"];
          const rank = (s) => {
            const i = STAGE_ORDER.indexOf(s || "");
            return i === -1 ? 999 : i;
          };
          return [...arr].sort((a, b) => {
            const d = rank(a.status) - rank(b.status);
            if (d !== 0) return globalSort === "stage_asc" ? d : -d;
            return numFromPo(a) - numFromPo(b);
          });
        }
      }
      return sortByPo(arr, poSort);
    };
    const rushed = data.filter((o) => rushRank(o) >= 2);
    const others = data.filter((o) => rushRank(o) < 2);
    rushed.sort((a, b) => rushRank(b) - rushRank(a));
    return [...rushed, ...applySort(others)];
  }, [data, poSort, globalSort, columnSort]);
  // Cross-stage matches — hits from OTHER tabs (excluding what's already in the current view).
  const currentIds = useMemo(() => new Set(rawData.map((o) => o.id)), [rawData]);
  const crossStageHits = useMemo(() => {
    if (!q) return [];
    return crossStageData.filter((o) => !currentIds.has(o.id) && matches(o));
  }, [crossStageData, currentIds, matches, q]);

  const sortedByAdvance = ["inventory_packing", "packing"].includes(stage);
  const qc = useQueryClient();
  // Product Transfer · Mark Received is auto-signed by the currently signed-in
  // team member — NO manual name input. The clicker is the receiver.
  const mySignature = user?.name || user?.email || "unknown";
  const markReceived = useMutation({
    mutationFn: (id) => api.post(`/orders/${id}/transition`, {
      target_stage: "packing",
      signature: mySignature,
      reason: `Marked Received by ${mySignature}`,
    }),
    onSuccess: () => { qc.invalidateQueries(); toast.success(`Marked Received by ${mySignature}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const isFactory = user.role === "factory_1";
  const isPackingUser = user.role === "packing_orders";
  const isInvPackingUser = user.role === "inventory_packing";
  // For rendering the Start Packing / Ship action buttons on the Packing +
  // Inv Packing tables we treat super_admin and back_office as packing users
  // too — they need visibility and the ability to trigger shipment on behalf
  // of the packing station (e.g. after an emergency reroute).
  const canActOnPacking = isPackingUser || isInvPackingUser || isBackOffice;
  const isShipping = user.role === "shipping_order";

  // Ship a single order (Shipping user, shipping stage inline row)
  const shipRow = useMutation({
    mutationFn: ({ id, tracking, eta }) => api.post(`/mock/ship-label/${id}`, { tracking, eta }),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Shipping info saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const completeRow = useMutation({
    mutationFn: (id) => api.post(`/orders/${id}/transition`, { target_stage: "completed" }),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Order shipped via Shopify — Completed"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const divertRow = useMutation({
    mutationFn: (id) => api.post(`/orders/${id}/transition`, { target_stage: "inventory_packing" }),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Order moved to Station Inventory Packing"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  // Inv Packing → Shipping. Fired by the green Ship button that appears once
  // an inventory-packed order is "READY TO BE SHIPPED" (all 14 checks done).
  // Kept as a distinct mutation from shipRow (which creates a ShipStation
  // label) — this one just flips the stage so the row moves to the
  // Shipping queue where the label gets generated.
  const shipNow = useMutation({
    mutationFn: (id) => api.post(`/orders/${id}/transition`, { target_stage: "shipping" }),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Order moved to Shipping"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to ship"),
  });
  // 🏬 Pick-up orders: email the client + move to Order Fulfilled as READY FOR PICK UP.
  const pickupReady = useMutation({
    mutationFn: (id) => api.post(`/orders/${id}/pickup-ready`),
    onSuccess: (r) => {
      qc.invalidateQueries();
      toast.success(`🏬 ${t("Ready for pick up")} · ${r?.data?.emailed ? `${t("email sent to")} ${r.data.emailed}` : t("email sent")} → ${t("Order Fulfilled")}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to mark ready for pick up"),
  });
  // Shipping station boxes: several orders → one box number (per day) → one tracking #.
  const makeBox = useMutation({
    mutationFn: ({ ids, ...opts }) => api.post("/orders/box-assign", { ids, ...opts }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      // Clear the header search so the new box (with its tracking input) is visible right away.
      setSearch("");
      const d = r?.data || {};
      if (d.shipped) {
        toast.success(`📦 ${t("BOX")} ${d.box_number} · ${d.shipped_count} ${t("order(s)")} 🚚 ${d.tracking} · ETA ${(d.eta || "").slice(0, 10)}`);
        return;
      }
      toast.success(d.added_to_existing
        ? `➕ ${d.count} ${t("order(s)")} → ${t("BOX")} ${d.box_number} · ${d.box_date}`
        : `📦 ${t("BOX")} ${d.box_number} · ${d.count} ${t("order(s)")} · ${d.box_date}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to make box"),
  });
  const clearBox = useMutation({
    mutationFn: (ids) => api.post("/orders/box-clear", { ids }),
    onSuccess: () => { qc.invalidateQueries(); toast.success(t("Box cleared")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const boxNote = useMutation({
    mutationFn: ({ box_id, note }) => api.post("/orders/box-note", { box_id, note }),
    onSuccess: () => { qc.invalidateQueries(); toast.success(t("Box note saved")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to save note"),
  });
  const shipBox = useMutation({
    mutationFn: ({ box_id, tracking, eta }) => api.post("/orders/box-ship", { box_id, tracking, eta }),
    onSuccess: (r) => { qc.invalidateQueries(); toast.success(`🚚 ${r?.data?.count} ${t("order(s)")} ${t("in transit to warehouse with")} ${r?.data?.tracking}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to ship box"),
  });

  // ---------- HQ Check / HQ Order (Pending → export .xlsx → refill → skip F1) ----------
  // Both flows share the same checkbox toolbar UI. Pending Orders = HQ Check
  // (round-trip: export empty codes, sync when filled). Production Orders =
  // HQ Order (one-way export with codes + catalog + qty, stamps orders as
  // MANUAL PRODUCTION with the exporter's signature).
  const hqMode = stage === "pending" ? "check" : stage === "production" ? "order" : null;
  const showHqCheck = !!hqMode && isBackOffice;
  // Factory One production list: checkboxes to sign / print several orders at once.
  const prodSelect = isFactory && stage === "production";
  const showSelect = showHqCheck || prodSelect;
  const [hqSelected, setHqSelected] = useState(() => new Set());
  const hqToggle = (id) => setHqSelected((prev) => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });
  const hqSelectableIds = React.useMemo(
    () => {
      if (prodSelect) return (data || []).map((o) => o.id);
      if (hqMode === "check") return (data || []).filter((o) => !o.hq_check_sent).map((o) => o.id);
      if (hqMode === "order") return (data || []).filter((o) => !o.manual_production).map((o) => o.id);
      return [];
    },
    [data, hqMode, prodSelect]
  );
  const hqAllSelected = hqSelectableIds.length > 0 && hqSelectableIds.every((id) => hqSelected.has(id));
  const hqToggleAll = () => {
    if (hqAllSelected) setHqSelected(new Set());
    else setHqSelected(new Set(hqSelectableIds));
  };
  const hqCreateEndpoint = hqMode === "order" ? "/hq-order/create" : "/hq-check/create";
  // ── Factory One production actions (inline in the row + batch toolbar) ──
  const signBatch = useMutation({
    mutationFn: (ids) => api.post("/orders/factory-sign-batch", { ids, signature: mySignature }),
    onSuccess: (r) => { qc.invalidateQueries(); setHqSelected(new Set()); toast.success(`✍ ${r?.data?.count ?? 0} · ${t("Signed — now print the production sheet")}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to sign"),
  });
  const markPrinted = useMutation({
    mutationFn: (ids) => api.post("/orders/mark-printed", { ids }),
    onSuccess: (r) => { qc.invalidateQueries(); setHqSelected(new Set()); toast.success(`🖨 ${r?.data?.count ?? 0} · ${t("Production sheet printed")}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to mark printed"),
  });
  const printOrders = (orders) => {
    const printable = orders.filter((o) => o.factory_signature);
    if (printable.length === 0) return;
    if (!printProductionSheet({ orders: printable, user, lang, t })) { toast.error("Popup blocked — allow popups to print"); return; }
    markPrinted.mutate(printable.map((o) => o.id));
  };
  const completeOne = useMutation({
    mutationFn: (id) => api.post(`/orders/${id}/production-complete`, { signature: mySignature }),
    onSuccess: () => { qc.invalidateQueries(); toast.success(`✓ ${t("Sign Off & Transfer")}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const returnAdvance = useMutation({
    mutationFn: ({ id, reason }) => api.post(`/orders/${id}/transition`, { target_stage: "advance", reason: reason || "Returned to Advance from Production (pre-sign)" }),
    onSuccess: () => { qc.invalidateQueries(); toast.success(`↩ ${t("Return")} ${t("Advance Orders")}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const prodActions = prodSelect ? {
    sign: (id) => signBatch.mutate([id]),
    print: (o) => printOrders([o]),
    complete: (id) => completeOne.mutate(id),
    returnAdvance: (id) => {
      const reason = window.prompt(t("Reason for sending this order back? (optional)"), "");
      if (reason === null) return;
      returnAdvance.mutate({ id, reason });
    },
    busy: signBatch.isPending || markPrinted.isPending || completeOne.isPending || returnAdvance.isPending,
  } : null;
  const selectedOrders = React.useMemo(() => (data || []).filter((o) => hqSelected.has(o.id)), [data, hqSelected]);
  const selectedAwaiting = selectedOrders.filter((o) => productionSubstage(o) === "awaiting");
  const selectedPrintable = selectedOrders.filter((o) => productionSubstage(o) === "to_print" || productionSubstage(o) === "in_production");
  const allToPrint = React.useMemo(() => (data || []).filter((o) => productionSubstage(o) === "to_print"), [data]);
  const hqCreate = useMutation({
    mutationFn: (factory) => api.post(hqCreateEndpoint, { order_ids: Array.from(hqSelected), factory }),
    onSuccess: (r) => {
      const d = r.data;
      qc.invalidateQueries();
      setHqSelected(new Set());
      const label = hqMode === "order" ? "HQ Order" : "HQ Check";
      // HQ Check reuses one tab per day — say "appended" the second+ time.
      const verb = d.appended ? "appended to" : "created tab";
      toast.success(`${label} · ${verb} "${d.tab_name}" · ${d.order_count} order(s)${d.signed_by ? ` · signed by ${d.signed_by}` : ""}`);
      if (d.workbook_url) window.open(d.workbook_url, "_blank", "noopener");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Create failed"),
  });
  const hqUpload = useMutation({
    mutationFn: async () => (await api.post("/hq-check/sync-from-drive")).data,
    onSuccess: (d) => {
      qc.invalidateQueries();
      const c = d.counts || {};
      const parts = [];
      if (c.advanced) parts.push(`${c.advanced} → Advance`);
      if (c.awaiting_approval) parts.push(`${c.awaiting_approval} filled (awaiting Pre-Order approval)`);
      if (c.partially_filled) parts.push(`${c.partially_filled} still partial`);
      if (c.na_marked) parts.push(`${c.na_marked} marked NA`);
      if (c.skipped) parts.push(`${c.skipped} skipped`);
      if (c.not_found) parts.push(`${c.not_found} PO not found`);
      if (c.ignored_tabs) parts.push(`${c.ignored_tabs} tab(s) ignored`);
      const summary = parts.length ? parts.join(" · ") : "nothing to apply";
      const fileErrors = (d.files || []).filter((f) => f.error);
      if (fileErrors.length) {
        const which = fileErrors.map((f) => f.factory_hint === "factory_2" ? "F2" : "F1").join(" & ");
        toast.warning(`Synced with issues on ${which} · ${summary}`);
      } else if ((c.fully_filled || 0) + (c.partially_filled || 0) + (c.na_marked || 0) > 0) {
        toast.success(`Codes synced from Drive · ${summary}`);
      } else {
        toast(`Drive sync complete · ${summary}`);
      }
      if (d.files?.length) console.info("Sync from Drive — per file:", d.files);
      if (d.not_found?.length) console.warn("Sync from Drive — PO not found:", d.not_found);
      if (d.skipped?.length) console.info("Sync from Drive — skipped:", d.skipped);
      if (d.na_marked?.length) console.info("Sync from Drive — NA marked:", d.na_marked);
      if (d.updated?.length) console.info("Sync from Drive — filled:", d.updated);
      if (d.ignored_tabs?.length) console.info("Sync from Drive — ignored tabs:", d.ignored_tabs);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Drive sync failed"),
  });

  // New Pre Order dialog
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ customer_id: "", po_number: "", vin: "", color: "", drive_folder_url: "" });
  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => (await api.get("/customers")).data,
    enabled: stage === "pre_order" && isBackOffice,
  });
  const createOrder = useMutation({
    mutationFn: (d) => api.post("/orders", d),
    onSuccess: () => { qc.invalidateQueries(); setNewOpen(false); setNewForm({ customer_id: "", po_number: "", vin: "", color: "", drive_folder_url: "" }); toast.success("Pre Order created"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  // New Lead dialog is now driven by ManualPreOrderDialog mode="lead" —
  // the local leadForm/createLead state below is intentionally kept out
  // so the "+ New Lead" and "Manual Pre Order" buttons share one UX.

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">{t("Orders")}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            {stage === "completed" && isFactoryRole ? t("Completed") : t(STAGE_LABEL[stage] || stage)}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {(stage === "completed" && isFactoryRole
              ? (data || []).filter((o) => !!o.packing_received_at).length
              : data.length)} {t("order(s)")}{sortedByAdvance && ` · ${t("ordered by Advance approval date")}`}
          </p>
          {stage === "production" && (
            <div className="mt-3 inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-sm" role="tablist" data-testid="prod-view-switch">
              <button type="button" role="tab" aria-selected={!batchView} onClick={() => setProdView("daily")} data-testid="prod-view-daily"
                className={`px-3 py-1.5 rounded transition-colors ${!batchView ? "bg-white shadow-sm font-semibold text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
                {t("Daily orders")} <span className="ml-1 font-mono text-xs text-slate-500" data-testid="prod-view-daily-count">{prodCounts.daily}</span>
              </button>
              <button type="button" role="tab" aria-selected={batchView} onClick={() => setProdView("batch")} data-testid="prod-view-batch"
                className={`px-3 py-1.5 rounded transition-colors ${batchView ? "bg-white shadow-sm font-semibold text-indigo-900" : "text-slate-500 hover:text-slate-800"}`}>
                📦 {t("Batch orders")} <span className="ml-1 font-mono text-xs text-slate-500" data-testid="prod-view-batch-count">{prodCounts.batch}</span>
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* Global sort — sits on the right, in-line with the action buttons */}
          <label className="text-[11px] uppercase tracking-widest font-mono text-slate-500">{t("Sort")}</label>
          <Select value={globalSort || "default"} onValueChange={(v) => setGlobalSort(v === "default" ? null : v)}>
            <SelectTrigger data-testid="global-sort" className="w-[220px] h-9 text-xs">
              <SelectValue placeholder={t("Default")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">{t("Default")}</SelectItem>
              <SelectItem value="po_asc">{t("Order # · Oldest first")}</SelectItem>
              <SelectItem value="po_desc">{t("Order # · Newest first")}</SelectItem>
              <SelectItem value="stage_asc">{t("Stage · A → Z (lead first)")}</SelectItem>
              <SelectItem value="stage_desc">{t("Stage · Z → A (cancelled first)")}</SelectItem>
              <SelectItem value="photos_asc">{t("Photos Received · Oldest first")}</SelectItem>
              <SelectItem value="photos_desc">{t("Photos Received · Newest first")}</SelectItem>
              <SelectItem value="waiting_reply_asc">{t("Waiting Reply · Oldest first")}</SelectItem>
            </SelectContent>
          </Select>
        {stage === "pre_order" && isBackOffice && (
          <>
            <ShopifyBulkImportDialog onImported={() => qc.invalidateQueries()} />
            <ManualPreOrderDialog
              mode="lead"
              triggerLabel="+ Manual Lead"
              onCreated={() => qc.invalidateQueries()}
            />

            <Dialog open={newOpen} onOpenChange={setNewOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-preorder-btn-legacy" className="bg-slate-900 hover:bg-slate-800 hidden">legacy</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New Pre Order (manual)</DialogTitle></DialogHeader>
                <div />
              </DialogContent>
            </Dialog>
            <ManualPreOrderDialog onCreated={() => qc.invalidateQueries()} />
          </>
        )}
        </div>
      </div>

      {stage === "shipping" && isShipping ? (
        <ShippingTable
          data={data}
          allData={rawData}
          searching={q.length > 0}
          isLoading={isLoading}
          stage={stage}
          onSave={(id, tracking, eta) => shipRow.mutate({ id, tracking, eta })}
          onMakeBox={(ids, opts) => makeBox.mutate({ ids, ...opts })}
          onClearBox={(ids) => clearBox.mutate(ids)}
          onShipBox={(box_id, tracking, eta) => shipBox.mutate({ box_id, tracking, eta })}
          onBoxNote={(box_id, note) => boxNote.mutate({ box_id, note })}
          busy={makeBox.isPending || clearBox.isPending || shipBox.isPending}
        />
      ) : isBoShipping ? (
        <BoShippingView data={data} isLoading={isLoading} stage={stage} />
      ) : stage === "completed" ? (
        <OrdersReceivedView
          data={data}
          isLoading={isLoading}
          stage={stage}
          isBackOffice={isBackOffice}
          isSuperAdmin={user.role === "super_admin"}
          hideShipped={isFactoryRole}
          onComplete={(id) => completeRow.mutate(id)}
          onDivert={(id) => divertRow.mutate(id)}
        />
      ) : (() => {
        const showCatCol = stage === "packing" || stage === "inventory_packing" || (stage === "pending" && isBackOffice) || batchView;
        const baseCols = (isBackOffice ? 8 : 6) + (stage === "inventory_packing" ? 1 : 0) + (showCatCol ? 1 : 0) + (showSelect ? 1 : 0) + (batchView ? 1 : 0);
        return (
      <div className="space-y-3">
      {prodSelect && (
        <div className="border border-emerald-300 bg-emerald-50 rounded-md px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="prod-toolbar">
          <button
            type="button"
            onClick={hqToggleAll}
            data-testid="prod-select-all"
            className="text-xs font-medium px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-100"
          >
            {hqAllSelected ? t("Clear selection") : t("Select all")}
          </button>
          <span className="text-xs font-mono text-slate-600" data-testid="prod-selected-count">
            {hqSelected.size} {t("selected")}
          </span>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <Button
              size="sm"
              disabled={selectedAwaiting.length === 0 || signBatch.isPending}
              onClick={() => signBatch.mutate(selectedAwaiting.map((o) => o.id))}
              data-testid="prod-sign-selected"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            >
              ✍ {t("Sign selected")} ({selectedAwaiting.length})
            </Button>
            <Button
              size="sm"
              disabled={selectedPrintable.length === 0 || markPrinted.isPending}
              onClick={() => printOrders(selectedPrintable)}
              data-testid="prod-print-selected"
              className="bg-violet-600 hover:bg-violet-700 text-white font-semibold"
            >
              🖨 {t("Print selected")} ({selectedPrintable.length})
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={allToPrint.length === 0 || markPrinted.isPending}
              onClick={() => printOrders(allToPrint)}
              data-testid="prod-print-all"
              className="border-violet-500 text-violet-800 hover:bg-violet-50 font-semibold"
            >
              🖨 {t("Print all To-Print")} ({allToPrint.length})
            </Button>
          </div>
        </div>
      )}
      {showHqCheck && (
        <div className="border border-slate-300 bg-slate-50 rounded-md px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="hq-check-toolbar">
          <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500 font-semibold">
            {hqMode === "order" ? t("HQ Order") : t("HQ Check")}
          </div>
          <button
            type="button"
            onClick={hqToggleAll}
            data-testid="hq-select-all"
            className="text-xs font-medium px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-100"
          >
            {hqAllSelected ? t("Clear selection") : t("Select all")}
          </button>
          <span className="text-xs font-mono text-slate-500" data-testid="hq-selected-count">
            {hqSelected.size} {t("selected")} · {hqSelectableIds.length} {t("available")}
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              size="sm"
              disabled={hqSelected.size === 0 || hqCreate.isPending}
              onClick={() => hqCreate.mutate("f1")}
              data-testid="hq-create-f1"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {hqCreate.isPending && hqCreate.variables === "f1"
                ? t("Adding tab…")
                : (hqMode === "order" ? t("🏭 Create F1 HQ Order") : t("📄 Create F1 HQ Check"))}
            </Button>
            <Button
              size="sm"
              disabled={hqSelected.size === 0 || hqCreate.isPending}
              onClick={() => hqCreate.mutate("f2")}
              data-testid="hq-create-f2"
              className="bg-slate-700 hover:bg-slate-800 text-white"
            >
              {hqCreate.isPending && hqCreate.variables === "f2"
                ? t("Adding tab…")
                : (hqMode === "order" ? t("🏭 Create F2 AOOA Order") : t("📄 Create F2 AOOA Check"))}
            </Button>
            {hqMode === "check" && (
              <Button
                size="sm"
                variant="outline"
                disabled={hqUpload.isPending}
                onClick={() => hqUpload.mutate()}
                data-testid="hq-upload-btn"
                className="border-emerald-600 text-emerald-800 hover:bg-emerald-50"
                title="Read the master HQ Check spreadsheet from Google Drive and fill matching orders. F1 vs F2 is detected from each tab's title. Only blanks are filled; any 'NA' in a code cell marks the whole order NA."
              >
                {hqUpload.isPending ? t("Syncing…") : t("🔄 Sync Codes from Drive")}
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead className="bg-slate-50">
            <tr>
              {showSelect && (
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={hqAllSelected}
                    onChange={hqToggleAll}
                    data-testid="hq-select-all-header"
                    className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                <ColumnSortHeader label="PO #" sortKey="po_number" state={columnSort} onChange={setColumnSort} testid="col-po" />
              </th>
              {isBackOffice && <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                <ColumnSortHeader label="Customer" sortKey="customer" state={columnSort} onChange={setColumnSort} testid="col-customer" />
              </th>}
              {isBackOffice && stage !== "pre_order" && <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                <ColumnSortHeader label="Factory" sortKey="factory" state={columnSort} onChange={setColumnSort} testid="col-factory" />
              </th>}
              <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                <ColumnSortHeader label="Vehicle" sortKey="vehicle" state={columnSort} onChange={setColumnSort} testid="col-vehicle" />
              </th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                <ColumnSortHeader label="Color" sortKey="color" state={columnSort} onChange={setColumnSort} testid="col-color" />
              </th>
              {batchView && (
                <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider" data-testid="batch-header">{t("Batch · Warehouse")}</th>
              )}
              {showCatCol && (
                <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider" data-testid="cat-header">
                  <ColumnSortHeader label="CAT #" sortKey="catalog" state={columnSort} onChange={setColumnSort} testid="col-catalog" />
                </th>
              )}
              {stage !== "pre_order" && (
                <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  <ColumnSortHeader label="Codes" sortKey="codes" state={columnSort} onChange={setColumnSort} testid="col-codes" />
                </th>
              )}
              {stage === "inventory_packing" && (
                <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  <ColumnSortHeader label="Logo" sortKey="logo" state={columnSort} onChange={setColumnSort} testid="col-logo" />
                </th>
              )}
              <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                <ColumnSortHeader label="Stage" sortKey="stage" state={columnSort} onChange={setColumnSort} testid="col-stage" />
              </th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={baseCols} className="px-4 py-10 text-center text-slate-400">{t("Loading…")}</td></tr>}
            {!isLoading && data.length === 0 && <tr><td colSpan={baseCols} className="px-4 py-10 text-center text-slate-400">{t("No orders in this stage.")}</td></tr>}
            {(() => {
              // Packing / Inventory-Packing users viewing their queue: split
              // into "Ready" (top) + "Completed" (bottom) so the operator
              // can see what still needs the wizard vs what's already packed.
              const isPackingQueue =
                (stage === "packing" && isPackingUser) ||
                (stage === "inventory_packing" && isInvPackingUser);
              if (isPackingQueue) {
                const doneField = stage === "inventory_packing" ? "inventory_packed_at" : "packed_at";
                // When user has an active PO sort, use it; otherwise keep the
                // default date-based sort for the "done" list.
                const ready = sortedData.filter((o) => !o[doneField]);
                const done = poSort
                  ? sortedData.filter((o) => !!o[doneField])
                  : data.filter((o) => !!o[doneField]).sort((a, b) => (b[doneField] || "").localeCompare(a[doneField] || ""));
                const renderRow = (o) => (
                  <PackingListRow key={o.id} o={o} stage={stage} user={user} isBackOffice={isBackOffice} isFactory={isFactory} isPackingUser={canActOnPacking} mySignature={mySignature} onMarkReceived={markReceived} showHqCheck={showSelect} hqChecked={hqSelected.has(o.id)} onHqToggle={hqToggle} shipNow={shipNow} pickupReady={pickupReady} prodActions={prodActions} batchView={batchView} />
                );
                const readyLabel = stage === "inventory_packing" ? t("📦 INV Packing Ready") : t("📦 Packing Ready");
                const doneLabel = stage === "inventory_packing" ? t("✓ Complete INV Packing") : t("✓ Complete Packing");
                return (
                  <>
                    <tr className="bg-amber-50/60 border-t border-amber-200">
                      <td colSpan={baseCols} className="px-4 py-2 text-[11px] uppercase tracking-widest font-mono text-amber-800 font-semibold" data-testid="section-packing-ready">{readyLabel} · {ready.length} {t("order(s)")}</td>
                    </tr>
                    {ready.length === 0 && <tr><td colSpan={baseCols} className="px-4 py-4 text-center text-slate-400 text-sm">{t("Queue clear — no orders waiting to be packed.")}</td></tr>}
                    {ready.map(renderRow)}
                    <tr className="bg-emerald-50/60 border-t border-emerald-200">
                      <td colSpan={baseCols} className="px-4 py-2 text-[11px] uppercase tracking-widest font-mono text-emerald-800 font-semibold" data-testid="section-complete-packing">{doneLabel} · {done.length} {t("order(s)")}</td>
                    </tr>
                    {done.length === 0 && <tr><td colSpan={baseCols} className="px-4 py-4 text-center text-slate-400 text-sm">{t("No packed history yet.")}</td></tr>}
                    {done.map(renderRow)}
                  </>
                );
              }
              return sortedData.map((o) => (
                <PackingListRow key={o.id} o={o} stage={stage} user={user} isBackOffice={isBackOffice} isFactory={isFactory} isPackingUser={canActOnPacking} mySignature={mySignature} onMarkReceived={markReceived} showHqCheck={showSelect} hqChecked={hqSelected.has(o.id)} onHqToggle={hqToggle} shipNow={shipNow} pickupReady={pickupReady} prodActions={prodActions} batchView={batchView} />
              ));
            })()}
            {/* Cross-stage search hits — orders matching the query that live in OTHER tabs */}
            {q.length >= 2 && crossStageHits.length > 0 && (
              <>
                <tr className="bg-blue-50/60 border-t border-blue-200">
                  <td colSpan={baseCols} className="px-4 py-2 text-[11px] uppercase tracking-widest font-mono text-blue-800 font-semibold" data-testid="section-cross-stage">
                    🔎 {t("Also found in other tabs")} · {crossStageHits.length} {crossStageHits.length === 1 ? t("match") : t("matches")}
                  </td>
                </tr>
                {sortByPo(crossStageHits, poSort).map((o) => (
                  <PackingListRow
                    key={`cross-${o.id}`}
                    o={o}
                    stage={o.status}
                    user={user}
                    isBackOffice={isBackOffice}
                    isFactory={isFactory}
                    isPackingUser={isPackingUser}
                    mySignature={mySignature}
                    onMarkReceived={markReceived}
                    shipNow={shipNow}
                    pickupReady={pickupReady}
                  />
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
      </div>
        );
      })()}
    </div>
  );
}

function ShippingTable({ data, allData, searching, isLoading, onSave, stage, onMakeBox, onClearBox, onShipBox, onBoxNote, busy }) {
  const { t } = useT();
  const [columnSort, setColumnSort] = useColumnSort(`shipping:${stage || "all"}`);
  const [selected, setSelected] = useState(() => new Set());
  const [boxDialogOpen, setBoxDialogOpen] = useState(false);
  const sorted = useMemo(() => {
    if (columnSort?.key && columnSort?.dir) {
      const ex = ORDER_COLUMN_EXTRACTORS[columnSort.key];
      if (ex) return sortByExtractor(data, columnSort.dir, ex);
    }
    return data;
  }, [data, columnSort]);
  // Boxed orders are grouped under one header row (one tracking # per box);
  // loose orders keep their own inputs + a checkbox to build the next box.
  // Rush ▸ Warranty / Reorder always float to the top of the loose list.
  const loose = useMemo(() => pinPriority(sorted.filter((o) => !o.box_id)), [sorted]);
  const boxes = useMemo(() => {
    const m = new Map();
    sorted.filter((o) => o.box_id).forEach((o) => {
      if (!m.has(o.box_id)) m.set(o.box_id, { id: o.box_id, number: o.box_number, date: o.box_date, note: o.box_note || "", orders: [] });
      const b = m.get(o.box_id);
      b.orders.push(o);
      if (!b.note && o.box_note) b.note = o.box_note;
    });
    return [...m.values()].sort((a, b) => (b.date || "").localeCompare(a.date || "") || (b.number || 0) - (a.number || 0));
  }, [sorted]);
  // Every open box (search-independent) — offered in the Make Box dialog.
  const openBoxes = useMemo(() => {
    const m = new Map();
    (allData || data).filter((o) => o.box_id).forEach((o) => {
      if (!m.has(o.box_id)) m.set(o.box_id, { id: o.box_id, number: o.box_number, date: o.box_date, note: o.box_note || "", count: 0 });
      m.get(o.box_id).count += 1;
    });
    return [...m.values()].sort((a, b) => (b.date || "").localeCompare(a.date || "") || (a.number || 0) - (b.number || 0));
  }, [allData, data]);
  const looseIds = loose.map((o) => o.id);
  const allLooseSelected = looseIds.length > 0 && looseIds.every((id) => selected.has(id));
  const toggle = (id) => setSelected((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  // Select all / clear only touches the VISIBLE rows — orders ticked under a
  // previous search stay selected so a box can be built across searches.
  const toggleAll = () => setSelected((prev) => {
    const s = new Set(prev);
    looseIds.forEach((id) => (allLooseSelected ? s.delete(id) : s.add(id)));
    return s;
  });
  // Selection is counted against the FULL list (not the search-filtered one).
  const selectedOrders = useMemo(
    () => (allData || data).filter((o) => !o.box_id && selected.has(o.id)),
    [allData, data, selected],
  );
  const selectedIds = selectedOrders.map((o) => o.id);
  const cols = 7;
  return (
    <div className="space-y-3">
      <div className="border border-sky-300 bg-sky-50 rounded-md px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="box-toolbar">
        <button type="button" onClick={toggleAll} data-testid="box-select-all" className="text-xs font-medium px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-100">
          {allLooseSelected ? t("Clear selection") : t("Select all")}
        </button>
        <span className="text-xs font-mono text-slate-600" data-testid="box-selected-count">{selectedIds.length} {t("selected")}</span>
        {selectedOrders.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="box-selected-chips">
            {selectedOrders.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => toggle(o.id)}
                title={t("Remove from selection")}
                data-testid={`box-chip-${o.id}`}
                className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-sky-900 bg-white border border-sky-300 rounded px-2 py-0.5 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
              >
                {o.po_number} <span aria-hidden="true">✕</span>
              </button>
            ))}
          </div>
        )}
        {searching && selectedOrders.length > 0 && (
          <span className="text-[11px] text-sky-800 italic" data-testid="box-search-hint">{t("Ticked orders stay selected while you search for more.")}</span>
        )}
        <Button
          size="sm"
          data-testid="make-box-btn"
          disabled={selectedIds.length === 0 || busy}
          onClick={() => setBoxDialogOpen(true)}
          className="ml-auto bg-sky-700 hover:bg-sky-800 text-white font-semibold"
        >
          📦 {t("Make Box")} ({selectedIds.length})
        </Button>
        <MakeBoxDialog
          open={boxDialogOpen}
          onOpenChange={setBoxDialogOpen}
          count={selectedIds.length}
          openBoxes={openBoxes}
          busy={busy}
          onConfirm={(opts) => { onMakeBox(selectedIds, opts); setSelected(new Set()); setBoxDialogOpen(false); }}
        />
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
      <table className="w-full text-sm" data-testid="shipping-table">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-3 py-3 w-8">
              <input type="checkbox" checked={allLooseSelected} onChange={toggleAll} data-testid="box-select-all-header" className="w-4 h-4 rounded border-slate-300" />
            </th>
            <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <ColumnSortHeader label="PO #" sortKey="po_number" state={columnSort} onChange={setColumnSort} testid="ship-col-po" />
            </th>
            <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <ColumnSortHeader label="Warehouse" sortKey="warehouse" state={columnSort} onChange={setColumnSort} testid="ship-col-warehouse" />
            </th>
            <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <ColumnSortHeader label="Shipped On" sortKey="shipped_on" state={columnSort} onChange={setColumnSort} testid="ship-col-shipped" />
            </th>
            <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <ColumnSortHeader label="Tracking #" sortKey="tracking" state={columnSort} onChange={setColumnSort} testid="ship-col-tracking" />
            </th>
            <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <ColumnSortHeader label="ETA" sortKey="eta" state={columnSort} onChange={setColumnSort} testid="ship-col-eta" />
            </th>
            <th className="px-4 py-3 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{t("Action")}</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && <tr><td colSpan={cols} className="px-4 py-10 text-center text-slate-400">{t("Loading…")}</td></tr>}
          {!isLoading && data.length === 0 && <tr><td colSpan={cols} className="px-4 py-10 text-center text-slate-400">{t("No pending shipments — every box is in transit or already in Orders Received.")}</td></tr>}
          {boxes.map((box) => (
            <React.Fragment key={box.id}>
              <BoxHeaderRow box={box} cols={cols} onShip={onShipBox} onClear={onClearBox} onNote={onBoxNote} busy={busy} />
              {box.orders.map((o) => (
                <ShippingRow key={o.id} order={o} onSave={onSave} stage={stage} inBox={box} onRemove={() => onClearBox([o.id])} busy={busy} />
              ))}
            </React.Fragment>
          ))}
          {loose.map((o) => (
            <ShippingRow key={o.id} order={o} onSave={onSave} stage={stage} checked={selected.has(o.id)} onToggle={() => toggle(o.id)} />
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function BoxHeaderRow({ box, cols, onShip, onClear, onNote, busy }) {
  const { t } = useT();
  const [tracking, setTracking] = useState("");
  const [eta, setEta] = useState(defaultEta());
  const [note, setNote] = useState(box.note || "");
  useEffect(() => { setNote(box.note || ""); }, [box.note]);
  const noteDirty = note.trim() !== (box.note || "").trim();
  return (
    <tr className="border-t-2 border-sky-400 bg-sky-50" data-testid={`box-row-${box.id}`}>
      <td className="px-3 py-3 text-center text-sky-700">📦</td>
      <td className="px-4 py-3" colSpan={3}>
        <div className="font-display font-black text-sky-900 text-lg leading-tight" data-testid={`box-title-${box.id}`}>
          {t("BOX")} {box.number}
        </div>
        <div className="text-[11px] font-mono text-sky-800">{box.date} · {box.orders.length} {t("order(s)")}</div>
        <div className="mt-1.5 flex items-center gap-1.5">
          <Input
            data-testid={`box-note-${box.id}`}
            placeholder={t("📝 Box note (visible in Orders Received)")}
            value={note}
            maxLength={300}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && noteDirty) onNote(box.id, note.trim()); }}
            className="max-w-[360px] h-8 text-xs bg-white"
          />
          {noteDirty && (
            <Button size="sm" variant="outline" className="h-8 border-sky-400 text-sky-800" data-testid={`box-note-save-${box.id}`} disabled={busy} onClick={() => onNote(box.id, note.trim())}>
              {t("Save note")}
            </Button>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <Input
          data-testid={`box-tracking-${box.id}`}
          placeholder={t("One tracking # for the whole box")}
          value={tracking}
          onChange={(e) => setTracking(e.target.value)}
          className="max-w-[240px] font-mono bg-white"
        />
      </td>
      <td className="px-4 py-3">
        <Input data-testid={`box-eta-${box.id}`} type="date" value={eta} onChange={(e) => setEta(e.target.value)} className="max-w-[170px] font-mono bg-white" />
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            data-testid={`box-clear-${box.id}`}
            disabled={busy}
            onClick={() => onClear(box.orders.map((o) => o.id))}
            className="border-slate-300 text-slate-600"
            title={t("Remove these orders from the box")}
          >
            ✕ {t("Unbox")}
          </Button>
          <Button
            size="sm"
            data-testid={`box-ship-${box.id}`}
            disabled={!tracking.trim() || busy}
            onClick={() => onShip(box.id, tracking.trim(), eta ? new Date(eta).toISOString() : "")}
            className="bg-sky-700 hover:bg-sky-800 text-white font-semibold"
          >
            🚚 {t("Ship Box")} ({box.orders.length})
          </Button>
        </div>
      </td>
    </tr>
  );
}

// Destination warehouse: USA-flag orders ship to the American warehouse,
// everything else to the Canadian one.
function WarehousePill({ order }) {
  const us = isUsOrder(order);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${us ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-red-50 text-red-800 border-red-200"}`}
      data-testid={`ship-warehouse-${order.id}`}
    >
      {us ? "USA" : "CANADA"}
    </span>
  );
}

function ShippingRow({ order, onSave, stage, checked = false, onToggle, inBox = null, onRemove, busy = false }) {
  const { t } = useT();
  const [tracking, setTracking] = useState(order.shipping_tracking_number || "");
  const [eta, setEta] = useState(order.shipping_eta ? order.shipping_eta.slice(0, 10) : "");
  React.useEffect(() => {
    setTracking(order.shipping_tracking_number || "");
    setEta(order.shipping_eta ? order.shipping_eta.slice(0, 10) : "");
  }, [order.shipping_tracking_number, order.shipping_eta]);
  const shippedOn = order.shipping_shipped_at ? order.shipping_shipped_at.slice(0, 10) : "—";
  const dirty = tracking !== (order.shipping_tracking_number || "") || eta !== (order.shipping_eta ? order.shipping_eta.slice(0, 10) : "");
  const priority = isPriorityOrder(order);
  if (inBox) {
    return (
      <tr className={priority ? PRIORITY_ROW_CLS : "border-t border-sky-100 bg-sky-50/40 hover:bg-sky-50"} data-testid={`ship-row-${order.id}`}>
        <td className="px-3 py-2 text-center text-sky-300">·</td>
        <td className="px-4 py-2 font-mono font-semibold text-slate-900">
          <div className="inline-flex items-center gap-2 flex-wrap pl-4">
            <Link to={`/order/${order.id}`} state={{ from: stage }} className="hover:underline" data-testid={`ship-open-${order.id}`}>{order.po_number}</Link>
            <UsFlag order={order} testid={`us-flag-ship-${order.id}`} />
            <PriorityTag order={order} testid={`ship-${order.id}`} />
          </div>
        </td>
        <td className="px-4 py-2"><WarehousePill order={order} /></td>
        <td className="px-4 py-2 font-mono text-slate-500">{shippedOn}</td>
        <td className="px-4 py-2 font-mono text-xs text-sky-800" colSpan={2}>📦 {t("BOX")} {inBox.number} · {t("uses the box tracking #")}</td>
        <td className="px-4 py-2 text-right">
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            data-testid={`box-remove-${order.id}`}
            title={t("Remove this order from the box")}
            className="text-xs font-medium px-2 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-red-50 hover:border-red-300 hover:text-red-700 disabled:opacity-40"
          >
            ✕ {t("Remove")}
          </button>
        </td>
      </tr>
    );
  }
  return (
    <tr className={order.ship_hold ? "border-t border-red-200 bg-red-50/60" : priority ? PRIORITY_ROW_CLS : "border-t border-slate-100 hover:bg-slate-50"} data-testid={`ship-row-${order.id}`}>
      <td className="px-3 py-3 text-center">
        <input type="checkbox" checked={checked} onChange={onToggle} disabled={!!order.ship_hold} title={order.ship_hold ? t("ON HOLD — cannot be boxed") : undefined} data-testid={`box-check-${order.id}`} className="w-4 h-4 rounded border-slate-300 disabled:opacity-30" />
      </td>
      <td className="px-4 py-3 font-mono font-semibold text-slate-900">
        <div className="inline-flex items-center gap-2 flex-wrap">
          <Link to={`/order/${order.id}`} state={{ from: stage }} className="hover:underline" data-testid={`ship-open-${order.id}`}>{order.po_number}</Link>
          <UsFlag order={order} testid={`us-flag-ship-${order.id}`} />
          <PriorityTag order={order} testid={`ship-${order.id}`} />
          <HoldChip o={order} />
          <StockChip o={order} />
        </div>
      </td>
      <td className="px-4 py-3"><WarehousePill order={order} /></td>
      <td className="px-4 py-3 font-mono text-slate-700" data-testid={`ship-date-${order.id}`}>{shippedOn}</td>
      <td className="px-4 py-3">
        <Input
          data-testid={`ship-tracking-${order.id}`}
          placeholder="Enter tracking #"
          value={tracking}
          onChange={(e) => setTracking(e.target.value)}
          className="max-w-[220px] font-mono"
        />
      </td>
      <td className="px-4 py-3">
        <Input
          data-testid={`ship-eta-${order.id}`}
          type="date"
          value={eta}
          onChange={(e) => setEta(e.target.value)}
          className="max-w-[170px] font-mono"
        />
      </td>
      <td className="px-4 py-3 text-right">
        <Button
          size="sm"
          data-testid={`ship-save-${order.id}`}
          disabled={!dirty || !tracking.trim()}
          onClick={() => onSave(order.id, tracking.trim(), eta ? new Date(eta).toISOString() : "")}
          className="bg-slate-900 hover:bg-slate-800"
        >
          {t("Save & Ship")}
        </Button>
      </td>
    </tr>
  );
}

function OrdersReceivedView({ data, isLoading, isBackOffice, isSuperAdmin, hideShipped, onComplete, onDivert, stage }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { t } = useT();
  const mySignature = user?.name || user?.email || "unknown";
  const isShippingRole = user?.role === "shipping_order";

  // Per-table PO # sort (Received section + Cancelled section maintain
  // independent directions). Persisted per-stage so the user's choice sticks
  // across navigation & reload.
  // Per-column sort for the Received + Cancelled sections (Feb 28, 2026
  // rule: every column header in every stage table must be clickable-to-sort).
  const [receivedPoSort, setReceivedPoSort] = usePersistedSort(`orders-po-sort:received:${stage || "all"}`);
  const [cancelledPoSort, setCancelledPoSort] = usePersistedSort(`orders-po-sort:cancelled:${stage || "all"}`);
  const [receivedColSort, setReceivedColSort] = useColumnSort(`received:${stage || "all"}`);
  const [cancelledColSort, setCancelledColSort] = useColumnSort(`cancelled:${stage || "all"}`);
  // Collapsible batch rows (BO/SA) — all collapsed by default.
  const [expanded, toggle] = useToggleSet();

  const [cancelStopQueue, setCancelStopQueue] = useState([]); // several cancel-pending orders received at once → one STOP after another
  const [holdStopQueue, setHoldStopQueue] = useState([]); // ⏸ on-hold orders received → 🛑 STOP: store, do not ship
  const markReceived = useMutation({
    mutationFn: ({ id, bin_number }) => api.post(`/orders/${id}/mark-received`, { bin_number, signature: mySignature }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      if (r?.data?.cancel_stop) {
        toast.error(t("Marked received — 🛑 CANCELLED ORDER: it stays in the warehouse, confirm the STOP"), { duration: 8000 });
        setCancelStopQueue((q) => (q.includes(r.data.id) ? q : [...q, r.data.id]));
        return;
      }
      if (r?.data?.ship_hold_stop) {
        toast.error(t("Marked received — ⏸ ON HOLD: store it in the warehouse, do not ship"), { duration: 8000 });
        setHoldStopQueue((q) => (q.includes(r.data.id) ? q : [...q, r.data.id]));
        return;
      }
      toast.success(r?.data?.us_envelope ? t("Marked received · ✉ USA order stays here — confirm logo, then print the envelope label") : t("Marked received → moved to Inventory Packing"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to mark received"),
  });

  const moveToInvPacking = useMutation({
    mutationFn: (id) => api.post(`/orders/${id}/transition`, { target_stage: "inventory_packing" }),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Order moved to Inventory Packing"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to move to Inv Packing"),
  });

  // Factory roles: show every order they touched (packing received OR cancelled)
  if (hideShipped) {
    const completed = (data || [])
      .filter((o) => !!o.packing_received_at || o.cancelled === true)
      .sort((a, b) => (b.packing_received_at || b.cancelled_at || "").localeCompare(a.packing_received_at || a.cancelled_at || ""));
    return (
      <div className="space-y-6" data-testid="orders-received-view">
        <FactoryCompletedTable orders={completed} isLoading={isLoading} stage={stage} />
      </div>
    );
  }

  // BO / Shipping / Super Admin: split into "To Be Received" vs "Received"
  const isCancelled = (o) => o.cancelled === true || o.status === "cancelled";
  const isReceived = (o) => !!o.received_at;
  const toReceive = (data || []).filter((o) => !isCancelled(o) && !isReceived(o));
  const received = (data || [])
    .filter((o) => !isCancelled(o) && isReceived(o))
    .sort((a, b) => (b.received_at || "").localeCompare(a.received_at || ""));
  const cancelled = (data || [])
    .filter(isCancelled)
    .sort((a, b) => (b.cancelled_at || b.received_at || "").localeCompare(a.cancelled_at || a.received_at || ""));
  // Overlay: column sort wins > PO sort > default date sort.
  const applyColSort = (list, colSort, poSort) => {
    if (colSort?.key && colSort?.dir) {
      const ex = ORDER_COLUMN_EXTRACTORS[colSort.key];
      if (ex) return sortByExtractor(list, colSort.dir, ex);
    }
    return poSort ? sortByPo(list, poSort) : list;
  };
  const receivedSorted = applyColSort(received, receivedColSort, receivedPoSort);
  const cancelledSorted = applyColSort(cancelled, cancelledColSort, cancelledPoSort);

  // Group by tracking number so shipping/BO see batches like the physical
  // package delivery. Received batches sort by arrival, newest first.
  const batchList = groupByTracking(toReceive).sort((a, b) => (b.shipped_at || "").localeCompare(a.shipped_at || ""));
  const receivedBatches = groupByTracking(received).sort((a, b) => (b.received_at || "").localeCompare(a.received_at || ""));
  const trackMeta = (b) => [
    { label: "left factory", value: b.shipped_at },
    { label: "eta", value: b.eta },
    { label: "arrival", value: b.received_at, cls: "text-emerald-700" },
  ];

  return (
    <div className="space-y-8" data-testid="orders-received-view">
      <CancelStockStopDialog orderId={cancelStopQueue[0] || null} open={cancelStopQueue.length > 0} onOpenChange={(v) => { if (!v) setCancelStopQueue((q) => q.slice(1)); }} />
      <HoldStopDialog orderId={holdStopQueue[0] || null} open={holdStopQueue.length > 0} onOpenChange={(v) => { if (!v) setHoldStopQueue((q) => q.slice(1)); }} />
      {/* SECTION 1 — TO BE RECEIVED */}
      <section data-testid="section-to-be-received">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-500 font-mono">{t("To Be Received")}</h2>
          <span className="text-xs font-mono text-slate-500" data-testid="to-be-received-count">{batchList.length} {t("batch(es)")} · {toReceive.length} {t("order(s)")}</span>
        </div>
        {isLoading && <div className="text-slate-400 text-sm">{t("Loading…")}</div>}
        {!isLoading && batchList.length === 0 && (
          <div className="border border-dashed border-slate-300 rounded-md p-6 text-center text-slate-400 text-sm">{t("Nothing waiting to be received.")}</div>
        )}
        <div className={isShippingRole ? "space-y-4" : "space-y-2"}>
          {batchList.map((b) => (
            isShippingRole ? (
              <ShippingBatchCard key={b.key} batchKey={b.key} batch={b} stage={stage} onMarkReceived={markReceived} />
            ) : (
              <BackOfficeBatchCard
                key={b.key}
                batchKey={b.key}
                batch={b}
                stage={stage}
                onMarkReceived={markReceived}
                open={expanded.has(`in-${b.key}`)}
                onToggle={() => toggle(`in-${b.key}`)}
              />
            )
          ))}
        </div>
      </section>

      {/* SECTION 2 — RECEIVED */}
      <section data-testid="section-received">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-emerald-700 font-mono">{t("Received")}</h2>
          <span className="text-xs font-mono text-slate-500" data-testid="received-count">{!isShippingRole && <>{receivedBatches.length} {t("batch(es)")} · </>}{received.length} {t("order(s)")}</span>
        </div>
        {isShippingRole ? (
        <div className="bg-white border border-emerald-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="received-table">
            <thead className="bg-emerald-50">
              <tr>
                <th className="px-4 py-2 text-left text-[10px] font-semibold text-emerald-800 uppercase tracking-wider">
                  <ColumnSortHeader label="PO #" sortKey="po_number" state={receivedColSort} onChange={setReceivedColSort} testid="rcv-col-po" />
                </th>
                <th className="px-4 py-2 text-left text-[10px] font-semibold text-emerald-800 uppercase tracking-wider">
                  <ColumnSortHeader label="Vehicle" sortKey="vehicle" state={receivedColSort} onChange={setReceivedColSort} testid="rcv-col-vehicle" />
                </th>
                <th className="px-4 py-2 text-left text-[10px] font-semibold text-emerald-800 uppercase tracking-wider">
                  <ColumnSortHeader label="Tracking" sortKey="tracking" state={receivedColSort} onChange={setReceivedColSort} testid="rcv-col-tracking" />
                </th>
                <th className="px-4 py-2 text-left text-[10px] font-semibold text-emerald-800 uppercase tracking-wider">
                  <ColumnSortHeader label="Date Arrived" sortKey="received_on" state={receivedColSort} onChange={setReceivedColSort} testid="rcv-col-arrived" />
                </th>
                {!isShippingRole && (
                  <th className="px-4 py-2 text-left text-[10px] font-semibold text-emerald-800 uppercase tracking-wider">
                    <ColumnSortHeader label="Logo" sortKey="logo" state={receivedColSort} onChange={setReceivedColSort} testid="rcv-col-logo" />
                  </th>
                )}
                <th className="px-4 py-2 text-left text-[10px] font-semibold text-emerald-800 uppercase tracking-wider">
                  <ColumnSortHeader label="BIN #" sortKey="bin" state={receivedColSort} onChange={setReceivedColSort} testid="rcv-col-bin" />
                </th>
                <th className="px-4 py-2 text-left text-[10px] font-semibold text-emerald-800 uppercase tracking-wider">
                  <ColumnSortHeader label="Received By" sortKey="received_by" state={receivedColSort} onChange={setReceivedColSort} testid="rcv-col-by" />
                </th>
                {!isShippingRole && (
                  <th className="px-4 py-2 text-right text-[10px] font-semibold text-emerald-800 uppercase tracking-wider">{t("Actions")}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {received.length === 0 && (
                <tr><td colSpan={isShippingRole ? 5 : 8} className="px-4 py-8 text-center text-slate-400">{t("No orders received yet.")}</td></tr>
              )}
              {receivedSorted.map((o) => (
                <ReceivedRow
                  key={o.id}
                  o={o}
                  stage={stage}
                  showLogo={!isShippingRole}
                  showActions={!isShippingRole}
                  onDivert={() => moveToInvPacking.mutate(o.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
        ) : (
        <div className="space-y-2" data-testid="received-batches">
          {!isLoading && receivedBatches.length === 0 && (
            <div className="border border-dashed border-slate-300 rounded-md p-6 text-center text-slate-400 text-sm">{t("No orders received yet.")}</div>
          )}
          {receivedBatches.map((b) => (
            <BatchRow
              key={b.key}
              tid="rcv-batch"
              id={b.key}
              open={expanded.has(`rcv-${b.key}`)}
              onToggle={() => toggle(`rcv-${b.key}`)}
              badge="received"
              title={b.tracking || t("(no tracking)")}
              tracking={b.tracking}
              box={b.box}
              meta={trackMeta(b)}
              count={b.orders.length}
            >
              <BoxGroups
                orders={b.orders}
                gridCls="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2"
                testidPrefix={`rcv-batch-box-${b.key}`}
                renderOrder={(o) => <ReceivedChip key={o.id} o={o} stage={stage} onDivert={() => moveToInvPacking.mutate(o.id)} />}
              />
            </BatchRow>
          ))}
        </div>
        )}
      </section>

      {/* SECTION 3 — CANCELLED / REFUNDED (kept visible for BO/SA audit) */}
      {cancelled.length > 0 && (
        <section data-testid="section-cancelled">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-widest text-red-700 font-mono">{t("Cancelled / Refunded")}</h2>
            <span className="text-xs font-mono text-slate-500">{cancelled.length} {t("order(s)")}</span>
          </div>
          <div className="bg-red-50/60 border border-red-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-red-100/60">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] font-semibold text-red-800 uppercase tracking-wider">
                    <ColumnSortHeader label="PO #" sortKey="po_number" state={cancelledColSort} onChange={setCancelledColSort} testid="cnl-col-po" />
                  </th>
                  <th className="px-4 py-2 text-left text-[10px] font-semibold text-red-800 uppercase tracking-wider">
                    <ColumnSortHeader label="Vehicle" sortKey="vehicle" state={cancelledColSort} onChange={setCancelledColSort} testid="cnl-col-vehicle" />
                  </th>
                  <th className="px-4 py-2 text-left text-[10px] font-semibold text-red-800 uppercase tracking-wider">
                    <ColumnSortHeader label="Reason" sortKey="cancel_reason" state={cancelledColSort} onChange={setCancelledColSort} testid="cnl-col-reason" />
                  </th>
                  <th className="px-4 py-2 text-left text-[10px] font-semibold text-red-800 uppercase tracking-wider">
                    <ColumnSortHeader label="Cancelled On" sortKey="cancelled_on" state={cancelledColSort} onChange={setCancelledColSort} testid="cnl-col-date" />
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {cancelledSorted.map((o) => (
                  <CancelledRow
                    key={o.id}
                    o={o}
                    stage={stage}
                    isSuperAdmin={isSuperAdmin}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

// One row for the "To Be Received" section: Logo · PO · Vehicle · Tracking # ·
// ShipStation label button · Mark Received checkbox + BIN # · INV PACKING button.
// ---------- Batch components for "To Be Received" ----------

// Shipping Desk view — read-only batch card: single-line header with the
// tracking number on the left, "Waiting for Arrival" pill pushed to the
// right; orders inside are shown as PO-# chips that wrap onto as many
// lines as needed. Shipping Desk cannot confirm receipt — Back Office does.
function ShippingBatchCard({ batchKey, batch, stage, onMarkReceived }) {
  const { t } = useT();
  const eta = batch.eta ? batch.eta.slice(0, 10) : null;
  const shipped = batch.shipped_at ? batch.shipped_at.slice(0, 10) : null;
  const box = batch.box;

  return (
    <div className="border border-blue-200 bg-blue-50/60 rounded-md p-4" data-testid={`shipping-batch-${batchKey}`}>
      {/* Single-line header — tracking on the left, status pill on the right */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono font-bold text-slate-900 text-base whitespace-nowrap" data-testid={`shipping-batch-tracking-${batchKey}`}>
          {box ? <span className="text-sky-800 mr-2" data-testid={`shipping-batch-box-${batchKey}`}>📦 {box.label || <>{t("BOX")} {box.number} · {box.date}</>}</span> : null}
          {batch.tracking || "(no tracking)"}
        </span>
        <span className="text-xs font-mono text-slate-600 whitespace-nowrap">
          {shipped && <>left factory <span className="text-slate-900">{shipped}</span> · </>}
          {eta && <>ETA <span className="text-slate-900">{eta}</span> · </>}
          <span className="text-slate-900">{batch.orders.length}</span> order(s)
        </span>
        <span className="ml-auto inline-flex items-center gap-2">
          {batch.tracking && !batch.anyReceived && (
            <UndoShipmentButton tracking={batch.tracking} count={batch.orders.length} testid={`undo-batch-${batchKey}`} />
          )}
          <span
            className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border bg-amber-50 text-amber-800 border-amber-300 whitespace-nowrap"
            data-testid={`shipping-batch-status-${batchKey}`}
          >
            {t("Waiting for Arrival")}
          </span>
        </span>
      </div>
      {/* PO-# chips — divided by box when the batch holds several boxes */}
      <div className="mt-3" data-testid={`shipping-batch-orders-${batchKey}`}>
        <BoxGroups
          orders={batch.orders}
          gridCls="flex flex-wrap gap-2"
          undoTracking={batch.anyReceived ? null : batch.tracking}
          testidPrefix={`shipping-batch-box-${batchKey}`}
          renderOrder={(o) => (
            <Link
              key={o.id}
              to={`/order/${o.id}`}
              state={{ from: stage }}
              data-testid={`shipping-batch-order-${o.id}`}
              className="font-mono font-semibold text-slate-900 text-sm border border-slate-200 rounded bg-white px-2.5 py-1 hover:bg-slate-100 hover:border-slate-400 transition-colors inline-flex items-center gap-1.5"
            >
              <span>{o.po_number}</span>
              <UsFlag order={o} testid={`us-flag-shipbatch-${o.id}`} />
              {o.cancel_pending && <span className="rounded bg-red-600 text-white text-[9px] font-black px-1.5 py-0.5 tracking-wider" data-testid={`cancel-pending-shipbatch-${o.id}`} title="Cancelled — stays in the warehouse on arrival (STOP when received)">🛑 CANCELLED → STOCK</span>}
            </Link>
          )}
        />
      </div>
    </div>
  );
}

// Back Office view — collapsible batch row (same format as Shipment Batches)
// for RECEIVING only. One shared BIN # is entered on the batch header and
// applied to every order ticked inside the batch. Post-receiving actions
// (ShipStation / INV PACKING) live in the "Received" section below.
function BackOfficeBatchCard({ batchKey, batch, stage, onMarkReceived, open, onToggle }) {
  const { t } = useT();
  const [selected, setSelected] = useState({}); // { orderId: bool }
  const [bin, setBin] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleAll = () => {
    const allOn = batch.orders.every((o) => selected[o.id]);
    const next = {};
    if (!allOn) batch.orders.forEach((o) => { next[o.id] = true; });
    setSelected(next);
  };

  const receiveSelected = async () => {
    const ids = batch.orders.filter((o) => selected[o.id]).map((o) => o.id);
    if (ids.length === 0) { toast.error("Tick at least one order to receive"); return; }
    setBusy(true);
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await onMarkReceived.mutateAsync({ id, bin_number: bin.trim() });
      }
      setSelected({});
      setBin("");
    } finally {
      setBusy(false);
    }
  };

  const anySelected = batch.orders.some((o) => selected[o.id]);
  const allSelected = batch.orders.every((o) => selected[o.id]);

  const right = (
    <div className="flex items-center gap-2 py-2 flex-wrap">
      {batch.tracking && !batch.anyReceived && (
        <UndoShipmentButton tracking={batch.tracking} count={batch.orders.length} testid={`undo-batch-${batchKey}`} />
      )}
      <Input
        className="w-28 font-mono h-8"
        placeholder="BIN #"
        value={bin}
        onChange={(e) => setBin(e.target.value)}
        data-testid={`bo-batch-bin-${batchKey}`}
      />
      <label className="inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer select-none text-slate-600 whitespace-nowrap">
        <input
          type="checkbox"
          className="w-3.5 h-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
          checked={allSelected}
          onChange={toggleAll}
          data-testid={`bo-batch-select-all-${batchKey}`}
        />
        {t("Select all")}
      </label>
      <Button
        size="sm"
        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
        onClick={receiveSelected}
        disabled={busy || !anySelected}
        data-testid={`bo-batch-receive-${batchKey}`}
      >
        {busy ? t("Saving…") : t("✓ Receive Selected")}
      </Button>
    </div>
  );

  return (
    <BatchRow
      tid="bo-batch"
      id={batchKey}
      open={open}
      onToggle={onToggle}
      badge="shipped"
      title={batch.tracking || t("(no tracking)")}
      tracking={batch.tracking}
      box={batch.box}
      meta={[{ label: "shipped", value: batch.shipped_at }, { label: "eta", value: batch.eta }, { label: "arrival", value: null }]}
      count={batch.orders.length}
      right={right}
    >
      <BoxGroups
        orders={batch.orders}
        undoTracking={batch.anyReceived ? null : batch.tracking}
        testidPrefix={`bo-batch-box-${batchKey}`}
        groupExtra={(g) => {
          const all = g.orders.every((o) => selected[o.id]);
          return (
            <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-sky-900 bg-white border border-sky-300 rounded px-2 py-0.5 cursor-pointer select-none" title={t("Tick / untick every order in this box")}>
              <input
                type="checkbox"
                className="w-3.5 h-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={all}
                onChange={(e) => setSelected((s) => { const n = { ...s }; g.orders.forEach((o) => { n[o.id] = e.target.checked; }); return n; })}
                data-testid={`bo-box-check-${g.box_id}`}
              />
              {t("Whole box")}
            </label>
          );
        }}
        renderOrder={(o) => (
          <BackOfficeBatchRow
            key={o.id}
            o={o}
            stage={stage}
            selected={!!selected[o.id]}
            onToggle={(v) => setSelected((s) => ({ ...s, [o.id]: v }))}
          />
        )}
      />
    </BatchRow>
  );
}

// PO chip with a receive checkbox — click anywhere on the chip to tick it,
// click the PO # to open the order.
function BackOfficeBatchRow({ o, stage, selected, onToggle }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onToggle(!selected)}
      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onToggle(!selected); } }}
      className={`flex items-center gap-2 border rounded px-2 py-1.5 cursor-pointer transition-colors ${selected ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200 hover:border-slate-400"}`}
      data-testid={`bo-batch-row-${o.id}`}
    >
      <input
        type="checkbox"
        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
        checked={selected}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onToggle(e.target.checked)}
        data-testid={`bo-batch-check-${o.id}`}
      />
      <Link
        to={`/order/${o.id}`}
        state={{ from: stage }}
        onClick={(e) => e.stopPropagation()}
        className="font-mono font-semibold text-slate-900 text-sm hover:underline inline-flex items-center gap-1.5 truncate"
        data-testid={`bo-batch-po-${o.id}`}
      >
        <span>{o.po_number}</span>
        <UsFlag order={o} testid={`us-flag-bobatch-${o.id}`} />
        {o.cancel_pending && <span className="rounded bg-red-600 text-white text-[9px] font-black px-1.5 py-0.5 tracking-wider" data-testid={`cancel-pending-bobatch-${o.id}`} title="Cancelled — stays in the warehouse on arrival (STOP when received)">🛑 CANCELLED → STOCK</span>}
      </Link>
    </div>
  );
}

// Post-receiving actions (ShipStation / Shopify / Self Checkout / INV PACKING)
// shared by the Back Office batch chips and the legacy table row.
function ReceivedActions({ o, onDivert, envelope = false, hideDivert = false }) {
  const qc = useQueryClient();
  const { t } = useT();
  const [shipOpen, setShipOpen] = useState(false);
  const [shopifyOpen, setShopifyOpen] = useState(false);
  const [selfOpen, setSelfOpen] = useState(false);
  const hasLabel = !!(o.shipping_label_url || "").trim() || !!(o.shopify_label_url || "").trim();
  const sm = "h-7 px-2 text-xs";
  const refresh = () => qc.invalidateQueries();
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`received-actions-${o.id}`}>
      {o.shopify_label_url ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() => window.open(o.shopify_label_url, "_blank", "noopener")}
          data-testid={`received-shopify-label-${o.id}`}
          className={`${sm} border-emerald-700 text-emerald-800 hover:bg-emerald-50`}
        >
          {t("🖨️ Shopify label")}
        </Button>
      ) : (
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShipOpen(true)}
        data-testid={`received-shipstation-${o.id}`}
        className={`${sm} border-slate-900 text-slate-900 hover:bg-slate-100`}
      >
        {hasLabel ? t("🖨️ Reprint") : t("🚚 ShipStation")}
      </Button>
      )}
      {!hasLabel && (
        <>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShopifyOpen(true)}
            data-testid={`received-shopify-${o.id}`}
            className={`${sm} border-emerald-700 text-emerald-800 hover:bg-emerald-50`}
          >
            {t("🛍 Shopify")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSelfOpen(true)}
            data-testid={`received-self-${o.id}`}
            className={`${sm} border-slate-600 text-slate-700 hover:bg-slate-50`}
          >
            {t("✓ Self Checkout")}
          </Button>
        </>
      )}
      {(envelope || hideDivert) ? null : o.status === "inventory_packing" ? (
        <Badge className="bg-orange-100 text-orange-800 border border-orange-300 font-semibold text-[10px] uppercase tracking-widest self-center" data-testid={`received-in-inv-packing-${o.id}`}>
          {t("📦 INV PACKING")}
        </Badge>
      ) : (
        <Button
          size="sm"
          onClick={onDivert}
          data-testid={`received-inv-packing-${o.id}`}
          className={`${sm} bg-orange-500 hover:bg-orange-600 text-white`}
        >
          {t("📦 INV PACKING")}
        </Button>
      )}
      {/* Dialogs render in a portal, but React still bubbles their clicks up
          to the <tr onClick=openOrder> that owns this row — a tap on the
          pop-up's padding navigated away and "closed" it. Stop that here. */}
      <span onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} data-testid={`received-dialogs-${o.id}`}>
        {shipOpen && (
          <ShipStationDialog order={o} open={shipOpen} onOpenChange={setShipOpen} onSuccess={refresh} />
        )}
        {shopifyOpen && (
          <ShipFromShopifyDialog
            order={o}
            open={shopifyOpen}
            onOpenChange={setShopifyOpen}
            onSuccess={refresh}
          />
        )}
        {selfOpen && (
          <SelfCheckoutDialog
            order={o}
            open={selfOpen}
            onOpenChange={setSelfOpen}
            onSuccess={refresh}
          />
        )}
      </span>
    </div>
  );
}

// USA-flag order after receiving → envelope flow: confirm the logo label
// against its picture (skipped when the order has no logo), then print the
// envelope label via ShipStation or Shopify.
function UsEnvelopeActions({ o }) {
  const qc = useQueryClient();
  const { t } = useT();
  const { data: m } = useLogoLabel(o.logo, o.brand);
  const hasLogo = !!(o.logo || "").trim();
  const confirmed = !hasLogo || !!o.logo_confirmed_at;
  const confirm = useMutation({
    mutationFn: () => api.post(`/orders/${o.id}/confirm-logo`, { logo_name: m?.name || o.logo }),
    onSuccess: () => { qc.invalidateQueries(); toast.success(`✓ ${t("Logo confirmed")} · ${o.po_number}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to confirm logo"),
  });
  // USA order that slipped into Inventory Packing (received before the envelope rule) → pull it back here.
  const reclaim = useMutation({
    mutationFn: () => api.post(`/orders/${o.id}/transition`, { target_stage: "completed", reason: "✉ USA envelope order — kept in Orders Received (pulled back from Inventory Packing)" }),
    onSuccess: () => { qc.invalidateQueries(); toast.success(`✉ ${o.po_number} ${t("kept in Orders Received")}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <div className="space-y-1.5" data-testid={`envelope-flow-${o.id}`}>
      {o.status === "inventory_packing" && (
        <div className="flex items-center gap-2 flex-wrap text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`envelope-in-invpack-${o.id}`}>
          ⚠ {t("This USA order is in Inventory Packing")}
          <Button size="sm" onClick={() => reclaim.mutate()} disabled={reclaim.isPending} className="h-6 px-2 text-[11px] bg-sky-700 hover:bg-sky-800 text-white" data-testid={`envelope-reclaim-${o.id}`}>
            ✉ {t("Keep here for envelope")}
          </Button>
        </div>
      )}
      {hasLogo && (
        <div className="flex items-center gap-2 flex-wrap text-xs" data-testid={`envelope-logo-${o.id}`}>
          <span className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">{t("Logo")}</span>
          <span className="font-semibold text-slate-900" data-testid={`envelope-logo-name-${o.id}`}>{m?.name || o.logo}</span>
          <LogoLabelPreview logo={o.logo} brand={o.brand} className="h-8 w-14" testid={`envelope-logo-preview-${o.id}`} />
          {o.logo_confirmed_at ? (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase border bg-emerald-50 text-emerald-700 border-emerald-200" title={`${o.logo_confirmed_by || ""} · ${(o.logo_confirmed_at || "").slice(0, 10)}`} data-testid={`envelope-logo-confirmed-${o.id}`}>
              ✓ {t("Logo confirmed")}
            </span>
          ) : (
            <Button size="sm" onClick={() => confirm.mutate()} disabled={confirm.isPending} className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`envelope-confirm-logo-${o.id}`}>
              {confirm.isPending ? t("Saving…") : `✓ ${t("Confirm logo")}`}
            </Button>
          )}
        </div>
      )}
      {confirmed ? (
        <ReceivedActions o={o} envelope />
      ) : (
        <div className="text-[11px] text-slate-500 italic" data-testid={`envelope-wait-${o.id}`}>{t("Confirm the logo to unlock the envelope label")}</div>
      )}
    </div>
  );
}

// Compact chip inside an expanded RECEIVED batch: PO · BIN # on top, date +
// receiver underneath, action buttons in a small row below.
function ReceivedChip({ o, stage, onDivert }) {
  const { t } = useT();
  const { user } = useAuth();
  const us = isUsOrder(o);
  return (
    <div className={`border rounded px-2.5 py-2 ${o.ship_hold ? "border-red-300 bg-red-50/60" : us ? "border-sky-300 bg-sky-50/40" : "border-slate-200 bg-white"}`} data-testid={`received-row-${o.id}`}>
      <div className="flex items-center justify-between gap-2">
        <Link
          to={`/order/${o.id}`}
          state={{ from: stage }}
          data-testid={`received-open-${o.id}`}
          className="font-mono font-semibold text-slate-900 text-sm hover:underline inline-flex items-center gap-1.5"
        >
          <span>{o.po_number}</span>
          <UsFlag order={o} testid={`us-flag-received-${o.id}`} />
        </Link>
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 whitespace-nowrap inline-flex items-center gap-2">
          {us && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase border bg-sky-100 text-sky-800 border-sky-300 tracking-wider" data-testid={`envelope-badge-${o.id}`}>✉ {t("Envelope")}</span>}
          BIN <span className="text-sm font-bold text-slate-900 tracking-normal" data-testid={`received-bin-${o.id}`}>{o.bin_number || "—"}</span>
        </span>
      </div>
      <div className="mt-0.5 text-[10px] font-mono text-slate-500">
        <span data-testid={`received-date-${o.id}`}>{(o.received_at || "").slice(0, 10) || "—"}</span>
        {o.received_by && <> · {o.received_by}</>}
      </div>
      <div className="mt-1.5">
        {o.is_stock_unit ? (
          <div className="flex flex-wrap items-center gap-1.5" data-testid={`received-stock-${o.id}`}>
            <StockChip o={o} />
            {stockInDue(o) && canStockIn(user, o.stock_unit?.dest) && <StockInButton orderId={o.id} due className="h-6 px-2 text-[10px]" />}
          </div>
        ) : o.ship_hold ? (
          <div className="flex flex-wrap items-center gap-1.5" data-testid={`received-hold-${o.id}`}>
            <HoldChip o={o} />
            {holdStopDue(o) && canAckHoldStop(user, o.ship_hold_info?.warehouse) && (
              <HoldStopButton orderId={o.id} due stored={!!o.ship_hold_info?.stored} className="h-6 px-2 text-[10px]" />
            )}
          </div>
        ) : us ? <UsEnvelopeActions o={o} /> : <ReceivedActions o={o} onDivert={onDivert} />}
      </div>
    </div>
  );
}

function ReceivedRow({ o, stage, showLogo, showActions, onDivert }) {
  const { user } = useAuth();
  return (
    <tr className={o.ship_hold ? "border-t border-red-200 bg-red-50/60 hover:bg-red-50" : "border-t border-emerald-100 hover:bg-emerald-50/40"} data-testid={`received-row-${o.id}`}>
      <td className="px-4 py-2 font-mono font-semibold text-slate-900">
        <div className="inline-flex items-center gap-2 flex-wrap">
          <Link to={`/order/${o.id}`} state={{ from: stage }} data-testid={`received-open-${o.id}`} className="hover:underline">
            {o.po_number}
          </Link>
          <UsFlag order={o} testid={`us-flag-received-${o.id}`} />
          <HoldChip o={o} />
          <StockChip o={o} />
          {o.is_stock_unit && stockInDue(o) && canStockIn(user, o.stock_unit?.dest) && <StockInButton orderId={o.id} due className="h-6 px-2 text-[10px]" />}
          {o.ship_hold && holdStopDue(o) && canAckHoldStop(user, o.ship_hold_info?.warehouse) && (
            <HoldStopButton orderId={o.id} due stored={!!o.ship_hold_info?.stored} className="h-6 px-2 text-[10px]" />
          )}
        </div>
      </td>
      <td className="px-4 py-2 text-slate-700">{[o.brand, o.model, o.year].filter(Boolean).join(" · ") || "—"}</td>
      <td className="px-4 py-2 font-mono text-xs text-slate-700">{o.shipping_tracking_number ? <TrackingLink number={o.shipping_tracking_number} carrier={o.shipping_carrier} testid={`received-tracking-${o.id}`} /> : "—"}</td>
      <td className="px-4 py-2 font-mono text-slate-700" data-testid={`received-date-${o.id}`}>{(o.received_at || "").slice(0, 10) || "—"}</td>
      {showLogo && (
        <td className="px-4 py-2"><LogoBadge order={o} size={56} testid={`received-logo-${o.id}`} /></td>
      )}
      <td className="px-4 py-2 font-mono font-bold text-slate-900" data-testid={`received-bin-${o.id}`}>{o.bin_number || <span className="text-slate-400 font-normal">—</span>}</td>
      <td className="px-4 py-2 text-xs text-slate-600">{o.received_by || "—"}</td>
      {showActions && (
        <td className="px-4 py-2 text-right">
          <div className="inline-flex justify-end">
            <ReceivedActions o={o} onDivert={onDivert} />
          </div>
        </td>
      )}
    </tr>
  );
}

// Logo badge — uses order.logo (custom LOGO text/URL) if set, otherwise falls
// back to the customer logo_url; if none, shows a subtle placeholder pill.
function LogoBadge({ order, size = 40, testid }) {
  const rawLogo = (order?.logo || "").trim();
  const custLogo = (order?.customer?.logo_url || "").trim();
  const isImage = (s) => /^https?:\/\//.test(s) || s.startsWith("data:image/");
  const src = isImage(rawLogo) ? rawLogo : (custLogo || null);
  const textLabel = !isImage(rawLogo) && rawLogo ? rawLogo : null;
  if (src) {
    return (
      <img
        src={src}
        alt="logo"
        style={{ width: size, height: size }}
        className="rounded-md border border-slate-200 object-contain bg-white p-1 shrink-0"
        data-testid={testid}
      />
    );
  }
  if (textLabel) {
    // Text-only logo — show the FULL label with no frame; bold uppercase,
    // sized proportionally so it reads as prominent as adjacent columns.
    const font = size >= 48 ? "text-base" : size >= 32 ? "text-xs" : "text-[11px]";
    return (
      <div
        className={`inline-block ${font} font-extrabold text-slate-900 uppercase tracking-tight whitespace-nowrap max-w-[110px] truncate`}
        data-testid={testid}
        title={textLabel}
      >
        {textLabel}
      </div>
    );
  }
  return (
    <div
      style={{ width: size, height: size }}
      className="rounded-md border border-dashed border-slate-300 bg-slate-50/60 flex items-center justify-center text-[9px] uppercase font-mono text-slate-400 shrink-0"
      data-testid={testid}
    >
      no logo
    </div>
  );
}

function FactoryCompletedTable({ orders, isLoading, stage }) {
  const { user } = useAuth();
  const [columnSort, setColumnSort] = useColumnSort(`completed:${stage || "all"}`);
  const sorted = useMemo(() => {
    if (columnSort?.key && columnSort?.dir) {
      const ex = ORDER_COLUMN_EXTRACTORS[columnSort.key];
      if (ex) return sortByExtractor(orders, columnSort.dir, ex);
    }
    return orders;
  }, [orders, columnSort]);
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
      <table className="w-full text-sm" data-testid="completed-table">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <ColumnSortHeader label="PO #" sortKey="po_number" state={columnSort} onChange={setColumnSort} testid="completed-col-po" />
            </th>
            <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <ColumnSortHeader label="Vehicle" sortKey="vehicle" state={columnSort} onChange={setColumnSort} testid="completed-col-vehicle" />
            </th>
            <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <ColumnSortHeader label="Tracking" sortKey="tracking" state={columnSort} onChange={setColumnSort} testid="completed-col-tracking" />
            </th>
            <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <ColumnSortHeader label="Completed On" sortKey="completed_on" state={columnSort} onChange={setColumnSort} testid="completed-col-date" />
            </th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {isLoading && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
          {!isLoading && orders.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No completed orders yet.</td></tr>}
          {sorted.map((o) => {
            const cancelled = o.cancelled === true || o.status === "cancelled";
            return (
              <tr
                key={o.id}
                className={`border-t border-slate-100 ${cancelled ? "bg-red-50/70 hover:bg-red-100/60" : "hover:bg-slate-50"}`}
                data-testid={`completed-row-${o.id}`}
              >
                <td className="px-4 py-3 font-mono font-semibold text-slate-900">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{o.po_number}</span>
                    <UsFlag order={o} testid={`us-flag-completed-${o.id}`} />
                    {o.cancel_pending && <CancelPendingBadge o={o} user={user} />}
                    {cancelled && (
                      <Badge className="bg-red-100 text-red-800 border border-red-300 font-semibold" title={o.cancel_reason || "Cancelled"}>✕ REFUNDED</Badge>
                    )}
                  </div>
                </td>
                <td className={`px-4 py-3 ${cancelled ? "text-red-900/70" : "text-slate-700"}`}>{[o.brand, o.model, o.year].filter(Boolean).join(" · ") || "—"}</td>
                <td className={`px-4 py-3 font-mono text-xs ${cancelled ? "text-red-900/60" : "text-slate-700"}`}>{o.shipping_tracking_number ? <TrackingLink number={o.shipping_tracking_number} carrier={o.shipping_carrier} testid={`fulfilled-tracking-${o.id}`} /> : "—"}</td>
                <td className={`px-4 py-3 font-mono ${cancelled ? "text-red-900/70" : "text-slate-700"}`}>
                  {(cancelled ? (o.cancelled_at || o.packing_received_at) : (o.packing_received_at || o.completed_at))?.slice(0, 10) || "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/order/${o.id}`}
                    state={{ from: stage }}
                    className={`text-sm underline ${cancelled ? "text-red-800 hover:text-red-900" : "text-slate-700 hover:text-slate-900"}`}
                  >Open →</Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ShipStation dialog now lives in `/components/ShipStationDialog.jsx` and is
// imported at the top of this file.

function BoShippingView({ data, isLoading, stage }) {
  const [expanded, toggle] = useToggleSet();
  const [q, setQ] = useState("");
  const s = q.trim().toLowerCase();
  const matchPo = (b) => !s || b.orders.some((o) => (o.po_number || "").toLowerCase().includes(s));

  // Pending (no tracking yet): one batch per day the orders entered Shipping
  const pending = (data || []).filter((o) => o.status === "shipping" && !o.shipping_tracking_number);
  const pendingMap = {};
  for (const o of pending) {
    const d = shippingEnteredDay(o);
    if (!pendingMap[d]) pendingMap[d] = { day: d, orders: [] };
    pendingMap[d].orders.push(o);
  }
  const pendingBatches = Object.values(pendingMap).map((b) => ({ ...b, orders: pinPriority(b.orders) })).filter(matchPo).sort((a, b) => b.day.localeCompare(a.day));

  // Tracked: one batch per tracking #
  const tracked = (data || []).filter((o) => !!o.shipping_tracking_number);
  const batches = groupByTracking(tracked)
    .filter((b) => !s || (b.tracking || "").toLowerCase().includes(s) || matchPo(b))
    .sort((a, b) => {
      // In-transit batches first, then received; within group most recent shipped date first
      if (a.allReceived !== b.allReceived) return a.allReceived ? 1 : -1;
      return (b.shipped_at || "").localeCompare(a.shipped_at || "");
    });

  const inShippingCount = batches.filter((b) => !b.allReceived).reduce((sum, b) => sum + b.orders.length, 0);
  const receivedCount = batches.filter((b) => b.allReceived).reduce((sum, b) => sum + b.orders.length, 0);
  const dashMeta = [{ label: "shipped", value: null }, { label: "eta", value: null }, { label: "arrival", value: null }];

  return (
    <div className="space-y-6" data-testid="bo-shipping-view">
      {/* Quick counters + batch search */}
      <div className="flex flex-wrap items-center gap-3">
        <StatChip label="Pending Shipping" value={pending.length} color="amber" testid="bo-count-pending" />
        <StatChip label="In transit" value={inShippingCount} color="blue" testid="bo-count-in-shipping" />
        <StatChip label="Received" value={receivedCount} color="emerald" testid="bo-count-received" />
        <div className="ml-auto">
          <Input
            data-testid="bo-batch-search"
            placeholder="Filter by tracking # or PO #…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-72"
          />
        </div>
      </div>

      {/* PENDING SHIPPING — one collapsible batch per day the orders entered Shipping */}
      <section data-testid="bo-pending-section">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 font-mono">Pending Shipping</h2>
          <span className="text-xs font-mono text-slate-500">{pendingBatches.length} batch(es) · {pending.length} order(s) awaiting tracking</span>
        </div>
        {isLoading && <div className="text-slate-400 text-sm">Loading…</div>}
        {!isLoading && pendingBatches.length === 0 && (
          <div className="border border-dashed border-slate-300 rounded-md p-6 text-center text-slate-400 text-sm">Nothing pending — every box is on its way to the warehouse.</div>
        )}
        <div className="space-y-2">
          {pendingBatches.map((b) => (
            <BatchRow
              key={b.day}
              tid="bo-pending-batch"
              id={b.day}
              open={expanded.has(`pending-${b.day}`)}
              onToggle={() => toggle(`pending-${b.day}`)}
              badge="pending"
              title={`Entered shipping ${b.day}`}
              meta={dashMeta}
              count={b.orders.length}
            >
              <div className={CHIP_GRID}>
                {b.orders.map((o) => (
                  <PoChip key={o.id} o={o} stage={stage} glyph="…" glyphCls="bg-amber-50 text-amber-700 border-amber-200" testid={`bo-pending-order-${o.id}`} />
                ))}
              </div>
            </BatchRow>
          ))}
        </div>
      </section>

      {/* BATCHES — one row per tracking # (collapsible) */}
      <section data-testid="bo-batches-section">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 font-mono">Shipment Batches</h2>
          <span className="text-xs font-mono text-slate-500">{batches.length} batch(es) · {tracked.length} order(s)</span>
        </div>
        {!isLoading && batches.length === 0 && (
          <div className="border border-dashed border-slate-300 rounded-md p-6 text-center text-slate-400 text-sm">No batches match.</div>
        )}
        <div className="space-y-2">
          {batches.map((b) => (
            <BatchRow
              key={b.key}
              tid="bo-batch"
              id={b.tracking}
              open={expanded.has(b.tracking)}
              onToggle={() => toggle(b.tracking)}
              badge={b.allReceived ? "received" : "shipped"}
              title={b.tracking}
              tracking={b.tracking}
              box={b.box}
              meta={[{ label: "shipped", value: b.shipped_at }, { label: "eta", value: b.eta }, { label: "arrival", value: b.received_at, cls: "text-emerald-700" }]}
              count={b.orders.length}
              right={!b.anyReceived ? <UndoShipmentButton tracking={b.tracking} count={b.orders.length} testid={`undo-batch-${b.tracking}`} /> : null}
            >
              <BoxGroups
                orders={b.orders}
                undoTracking={b.anyReceived ? null : b.tracking}
                testidPrefix={`bo-batch-box-${b.tracking}`}
                renderOrder={(o) => (
                  <PoChip
                    key={o.id}
                    o={o}
                    stage={stage}
                    glyph={o.received_at ? "✓" : "…"}
                    glyphCls={o.received_at ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200"}
                    testid={`bo-batch-order-${o.id}`}
                  />
                )}
              />
            </BatchRow>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatChip({ label, value, color, testid }) {
  const cls = {
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  }[color] || "bg-slate-50 text-slate-800 border-slate-200";
  return (
    <div className={`inline-flex items-baseline gap-2 border rounded-md px-3 py-1.5 ${cls}`} data-testid={testid}>
      <span className="text-[10px] uppercase tracking-widest font-mono">{label}</span>
      <span className="font-mono font-bold text-lg">{value}</span>
    </div>
  );
}

/** 🛑 Cancelled order whose set stays in a warehouse — red badge on the row; opens the STOP when it is due. */
function CancelPendingBadge({ o, user }) {
  const cp = o.cancel_pending_stock || {};
  const wh = cp.warehouse || (cp.rule === "on_arrival" ? (isUsOrder(o) ? "usa" : "canada") : cp.rule);
  const due = cp.stage === "now" || !!cp.stage_reached_at || !!o.packed_at || !!o.inventory_packed_at || !!o.received_at || ["completed", "fulfilled"].includes(o.status);
  if (due && canConfirmCancelStop(user, wh)) {
    return <CancelStopButton orderId={o.id} due className="h-6 px-2 text-[10px]" />;
  }
  return (
    <Badge className="bg-red-600 text-white border border-red-700 font-black" data-testid={`cancel-pending-${o.id}`} title={cp.reason || "Cancelled — set goes to stock after packing / receiving"}>
      🛑 CANCELLED → {String(wh || "stock").toUpperCase()} STOCK{due ? "" : ` · STOP ${cp.stage === "mark_received" ? "on arrival" : "after packing"}`}
    </Badge>
  );
}

function PackingListRow({ o, stage, user, isBackOffice, isFactory, isPackingUser, mySignature, onMarkReceived, showHqCheck, hqChecked, onHqToggle, shipNow, pickupReady, prodActions, batchView = false }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { t, lang } = useT();
  // Reactivate mutation — only used by the No Mold tab. Clears the cancelled
  // flag and reverts the order to its last non-cancelled status (usually advance).
  const reactivate = useMutation({
    mutationFn: () => api.post(`/orders/${o.id}/reactivate`, {}),
    onSuccess: (res) => {
      qc.invalidateQueries();
      const target = res?.data?.target_status || "advance";
      toast.success(`Reactivated → ${STAGE_LABEL[target] || target}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not reactivate"),
  });
  // Which per-stage flag hides the checkbox after the row has been "sent"
  // to an HQ workbook: HQ Check (pending) uses hq_check_sent, HQ Order
  // (production) uses manual_production.
  const hqDone = prodActions ? false : (stage === "production" ? o.manual_production : o.hq_check_sent);
  // Lead orders (both current leads and formerly-leads that were promoted)
  // get a persistent turquoise wash so BO can spot them at a glance.
  const isLead = o.status === "lead" || o.is_lead || o.originally_lead;
  // Independent rush flags — any combination is valid.
  const rushHi = !!o.rush_highlight || Number(o.rush_level || 0) >= 1;
  const rushTop = !!o.rush_top || Number(o.rush_level || 0) >= 2;
  const rushUrgent = !!o.rush_urgent || Number(o.rush_level || 0) >= 3;
  const anyRush = rushHi || rushTop || rushUrgent;
  const isNoMold = stage === "no_mold" || stage === "no-mold" || (Array.isArray(o.cancel_reason_ids) && o.cancel_reason_ids.includes("reason-no-mold"));
  const qcHold = !!o.qc_hold && o.status === "production";
  const onHold = !!o.on_hold;
  const isPriority = isPriorityOrder(o);
  let rowClass = "border-t border-slate-100 hover:bg-slate-50 cursor-pointer";
  if (qcHold) {
    // Quality Control — whole row painted red and pinned to the top.
    rowClass = "border-t border-red-400 bg-red-200/80 hover:bg-red-200 cursor-pointer";
  } else if (onHold) {
    rowClass = "border-t border-amber-300 bg-amber-100/80 hover:bg-amber-100 cursor-pointer";
  } else if (isNoMold && (stage === "no_mold" || stage === "no-mold")) {
    // No Mold tab — subtle red wash so the row reads as "blocked / cancelled".
    rowClass = "border-t border-red-200 bg-red-50/60 hover:bg-red-50 cursor-pointer";
  } else if (rushUrgent) {
    // Urgent — entire row painted light red so it screams "urgent".
    rowClass = "border-t border-red-300 bg-red-100/80 hover:bg-red-100 cursor-pointer";
  } else if (isPriority) {
    // Warranty replacement / Reorder — light red row, start to finish.
    rowClass = `${PRIORITY_ROW_CLS} cursor-pointer`;
  } else if (isLead) {
    rowClass = "border-t border-cyan-200 bg-cyan-50/70 hover:bg-cyan-100/60 cursor-pointer";
  } else if (hqDone && showHqCheck) {
    rowClass = "border-t border-slate-100 bg-indigo-50/40 hover:bg-indigo-50/70 cursor-pointer";
  }
  // Whole-row click opens the order — but leave interactive elements
  // (buttons, checkboxes, links) untouched so they still work.
  const openOrder = (e) => {
    // Ignore clicks that originated inside a control (button, input, a, label).
    if (e.target.closest("button, a, input, label, select, textarea, [role='button']")) return;
    navigate(`/order/${o.id}`, { state: { from: stage } });
  };
  return (
    <tr className={rowClass} data-testid={`order-row-${o.id}`} onClick={openOrder}>
      {showHqCheck && (
        <td className="px-3 py-3 w-8">
          {!hqDone ? (
            <input
              type="checkbox"
              checked={!!hqChecked}
              onChange={() => onHqToggle?.(o.id)}
              data-testid={`hq-check-${o.id}`}
              className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
            />
          ) : (
            <span className="inline-block w-4 h-4" title="Already exported" />
          )}
        </td>
      )}
      <td className={`px-4 py-3 font-mono font-semibold ${rushHi ? "text-red-700" : "text-slate-900"}`}>
        <div className="flex items-center gap-2 flex-wrap">
          {rushUrgent && (
            <span title="URGENT" data-testid={`rush-flag-${o.id}`} className="text-red-700 font-black">
              🚩
            </span>
          )}
          <span>{o.po_number}</span>
          <UsFlag order={o} />
          <PickupBadge order={o} />
          <PriorityTag order={o} />
          {qcHold && (
            <Badge className="bg-red-600 text-white border border-red-700 font-black text-[10px] uppercase tracking-widest" data-testid={`qc-badge-${o.id}`}>
              🔴 {t("QUALITY CONTROL")}
            </Badge>
          )}
          {onHold && (
            <Badge className="bg-amber-200 text-amber-900 border border-amber-400 font-bold text-[10px] uppercase tracking-widest" data-testid={`hold-badge-${o.id}`} title={holdReasonText(o)}>
              🛑 {t("ON HOLD")}
            </Badge>
          )}
          {anyRush && (
            <Badge
              className="bg-red-100 text-red-800 border border-red-300 font-semibold text-[10px] uppercase tracking-widest"
              data-testid={`rush-badge-${o.id}`}
              title={`Rush · ${[rushUrgent && "Urgent", rushTop && "Top", rushHi && "Highlight"].filter(Boolean).join(" + ")}`}
            >
              🚨 RUSH {[rushUrgent && "U", rushTop && "T", rushHi && "H"].filter(Boolean).join("+")}
            </Badge>
          )}
          {o.hq_check_sent && !o.hq_checked && (
            <Badge
              className="bg-indigo-100 text-indigo-800 border border-indigo-300 font-semibold text-[10px]"
              title={`HQ Check sent · ${o.hq_check_factory?.toUpperCase() || "?"} · tab ${o.hq_check_tab_name || "?"}`}
              data-testid={`hq-sent-${o.id}`}
            >
              📄 HQ CHECK · {o.hq_check_factory?.toUpperCase() || ""}
            </Badge>
          )}
          <DiscountChip order={o} />
        </div>
        {qcHold && (() => {
          // One-line QC summary for the factory: reason · reporter · response note · photos
          const line = [o.qc_reason, o.qc_reported_by && `${t("Reported by")} ${o.qc_reported_by}`, o.qc_note && `${t("Response")}: ${o.qc_note}`].filter(Boolean).join(" · ");
          return (
            <div className="mt-1 flex items-center gap-2 text-[11px] font-sans font-medium text-red-900 whitespace-nowrap" data-testid={`qc-line-${o.id}`} title={line}>
              <span className="truncate max-w-[180px] xl:max-w-[240px] 2xl:max-w-[520px]">{line}</span>
              <IssuePhotos photos={o.qc_photo_ids} size="h-7" testid={`qc-line-photos-${o.id}`} />
            </div>
          );
        })()}
        {!qcHold && o.defect_count > 0 && o.defect_note && (
          <div className="mt-1 text-[11px] font-sans font-semibold text-rose-800 whitespace-nowrap" data-testid={`defect-line-${o.id}`}
               title={(o.defect_history || []).map((d) => `D${d.n} · ${d.note}`).join("\n")}>
            ⚠ D{o.defect_count} · <span className="truncate inline-block max-w-[180px] xl:max-w-[240px] 2xl:max-w-[420px] align-bottom">{o.defect_note}</span>
          </div>
        )}
        {o.claim_id && (o.claim_defect_note || o.claim_solution_note) && (() => {
          // Claim replacement (PO-xxxx-W / -R): defect + solution in the viewer's language, other language on hover.
          const pick = (text, tr, trLang) => (lang === trLang && tr ? tr : text);
          const other = (text, tr, trLang) => (lang === trLang && tr ? text : tr);
          const defect = pick(o.claim_defect_note, o.claim_defect_translation, o.claim_defect_translation_lang);
          const solution = pick(o.claim_solution_note, o.claim_solution_translation, o.claim_solution_translation_lang);
          const title = [
            o.claim_defect_note && `${t("Defect")}: ${defect}${other(o.claim_defect_note, o.claim_defect_translation, o.claim_defect_translation_lang) ? ` / ${other(o.claim_defect_note, o.claim_defect_translation, o.claim_defect_translation_lang)}` : ""}`,
            o.claim_solution_note && `${t("Solution")}: ${solution}${other(o.claim_solution_note, o.claim_solution_translation, o.claim_solution_translation_lang) ? ` / ${other(o.claim_solution_note, o.claim_solution_translation, o.claim_solution_translation_lang)}` : ""}`,
          ].filter(Boolean).join("\n");
          return (
            <div className="mt-1 text-[11px] font-sans font-semibold text-red-800 whitespace-nowrap truncate max-w-[260px] xl:max-w-[360px] 2xl:max-w-[640px]" data-testid={`claim-line-${o.id}`} title={title}>
              {o.is_warranty ? "🛠" : "💳"} {t(o.is_warranty ? "Warranty of" : "Re-order of")} {o.replacement_of_po}
              {o.claim_defect_note ? ` · ${t("Defect")}: ${defect}` : ""}
              {o.claim_solution_note ? <span className="text-emerald-800"> · {t("Solution")}: {solution}</span> : null}
            </div>
          );
        })()}
        {o.is_reorder && !o.claim_id && o.reorder_of_po && (
          <div
            className="mt-1 text-[11px] font-sans font-semibold text-slate-700 whitespace-nowrap truncate max-w-[220px] xl:max-w-[300px] 2xl:max-w-[560px]"
            data-testid={`reorder-line-${o.id}`}
            title={`${t("Re-order of")} ${o.reorder_of_po}${o.reorder_reason ? ` · ${o.reorder_reason}` : ""}${o.reorder_received_color ? ` (${t("received")}: ${o.reorder_received_color})` : ""}`}
          >
            🔁 {t("Re-order of")} {o.reorder_of_po}{o.reorder_reason ? ` · ${o.reorder_reason}` : ""}{o.reorder_received_color ? ` (${t("received")}: ${o.reorder_received_color})` : ""}
          </div>
        )}
        {o.ships_inside?.order_id && (
          <div className={`mt-1 text-[11px] font-sans font-bold whitespace-nowrap truncate max-w-[260px] ${o.ships_inside.separated ? "text-emerald-800" : "text-sky-800"}`} data-testid={`inside-line-${o.id}`}>
            📦 {o.ships_inside.separated ? t("Separated from") : t("Inside box of")} {o.ships_inside.po_number}
          </div>
        )}
        {(o.carries || []).some((c) => !c.removed) && (
          <div className="mt-1 text-[11px] font-sans font-bold text-amber-800 whitespace-nowrap truncate max-w-[260px] xl:max-w-[360px]" data-testid={`carries-line-${o.id}`}
               title={(o.carries || []).filter((c) => !c.removed).map((c) => `${c.label} · ${c.po_number}`).join("\n")}>
            ➕ {t("Carries")} {(o.carries || []).filter((c) => !c.removed).map((c) => `${c.label} · ${c.po_number}`).join(", ")} — {t("take out before shipping to the customer")}
          </div>
        )}
        {Array.isArray(o.match_suggestions) && o.match_suggestions.length > 0 && (user.role === "back_office" || user.role === "super_admin") && (
          <div className="mt-1 text-[11px] font-sans font-semibold text-amber-800 whitespace-nowrap truncate max-w-[260px]" data-testid={`match-line-${o.id}`}
               title={o.match_suggestions.map((s) => `${s.po_number} · ${s.reason}`).join("\n")}>
            🔗 {t("Possible match")}: {o.match_suggestions.map((s) => s.po_number).join(", ")}
          </div>
        )}
        {o.reordered && o.reorder_po && (
          <div className="mt-1 text-[11px] font-sans font-semibold text-slate-700 whitespace-nowrap truncate max-w-[220px] xl:max-w-[300px] 2xl:max-w-[560px]" data-testid={`reordered-line-${o.id}`}>
            🔁 {t("Re-ordered")} → {o.reorder_po}{o.transferred_to_inventory ? ` · 📦 ${String(o.transferred_to_inventory.warehouse || "china").toUpperCase()} · ${o.transferred_to_inventory.cat_number || ""}` : ""}
          </div>
        )}
        {o.status === "advance" && o.production_needed?.at && (
          <div className="mt-1 text-[11px] font-sans font-black text-white bg-red-600 rounded px-1.5 py-0.5 inline-block whitespace-nowrap" data-testid={`production-needed-line-${o.id}`}
               title={`${t("Not found in")} ${WAREHOUSE_LABEL[o.production_needed.warehouse] || "—"} ${t("warehouse")} · ${o.production_needed.by || ""}${o.production_needed.note ? ` · ${o.production_needed.note}` : ""}`}>
            🏭 {t("NO INVENTORY")} · {t("PRODUCTION NEEDED")} · {WAREHOUSE_FLAG[o.production_needed.warehouse] || ""} {o.production_needed.cat_number || o.catalog_number || ""}
          </div>
        )}
        {["advance", "flagged"].includes(o.status) && o.vehicle_fit?.ok === false && !o.vehicle_fit?.acknowledged && (
          <div className="mt-1 text-[11px] font-sans font-black text-amber-950 bg-amber-300 rounded px-1.5 py-0.5 inline-block whitespace-nowrap" data-testid={`vehicle-mismatch-line-${o.id}`}
               title={`CAT # ${o.vehicle_fit.cat_number || ""} is for a different brand / model / year — approve with signature or assign another CAT #`}>
            ⚠ {t("VEHICLE MISMATCH")} · {o.vehicle_fit.cat_number || o.catalog_number || ""}
          </div>
        )}
        {o.ship_hold && (
          <div className="mt-1 inline-flex items-center gap-1" data-testid={`hold-line-${o.id}`}>
            <HoldChip o={o} />
            {holdStopDue(o) && canAckHoldStop(user, o.ship_hold_info?.warehouse) && <HoldStopButton orderId={o.id} due stored={!!o.ship_hold_info?.stored} className="h-6 px-2 text-[10px]" />}
          </div>
        )}
        {o.is_stock_unit && (
          <div className="mt-1 inline-flex items-center gap-1" data-testid={`stock-line-${o.id}`}>
            <StockChip o={o} />
            {stockInDue(o) && canStockIn(user, o.stock_unit?.dest) && <StockInButton orderId={o.id} due className="h-6 px-2 text-[10px]" />}
          </div>
        )}
        {["advance", "flagged"].includes(o.status) && o.spec_alert?.fields?.length > 0 && !o.spec_alert.acknowledged && (
          <div className="mt-1 text-[11px] font-sans font-black text-white bg-red-600 rounded px-1.5 py-0.5 inline-block whitespace-nowrap" data-testid={`spec-alert-chip-${o.id}`}
               title={`Order ${o.spec_alert.fields.map((f) => ({ rim_size: "rim size", spacer: "spacer type", spacer_qty: "spacer qty" }[f] || f)).join(" / ")} does not match catalog CAT # ${o.spec_alert.cat_number || ""} — repair the order info or approve with a signature`}>
            ⚠ {t("SPEC ALERT")} · {o.spec_alert.fields.map((f) => ({ rim_size: "RIM", spacer: "SPACER", spacer_qty: "QTY" }[f] || f)).join(" · ")} · {o.spec_alert.cat_number || o.catalog_number || ""}
          </div>
        )}
        {pickupPending(o) && (
          <div className="mt-1 text-[11px] font-sans font-semibold text-amber-900 whitespace-nowrap" data-testid={`pickup-line-${o.id}`}>
            📦 {t("TO PICK UP")} · {WAREHOUSE_FLAG[pickupInfo(o).warehouse]} {WAREHOUSE_LABEL[pickupInfo(o).warehouse]} · {FACTORY_SHORT[pickupInfo(o).factory] || "F1"}
            {pickupInfo(o).color_override && <span className="ml-1 text-red-700 font-black">· ⚠ {pickupInfo(o).color_override.toUpperCase()}</span>}
            {pickupInfo(o).from_no_color && <span className="ml-1 text-teal-700 font-black">· ◌ NO COLOR STOCK</span>}
          </div>
        )}
        {o.status === "pending" && o.codes_recheck_requested && (
          <div
            className="mt-1 text-[11px] font-sans font-semibold text-amber-800 whitespace-nowrap truncate max-w-[220px] xl:max-w-[300px] 2xl:max-w-[560px]"
            data-testid={`recheck-line-${o.id}`}
            title={`${t("Recheck codes")} #${o.recheck_attempt || 1} · ${o.codes_recheck_requested_by || ""}${o.codes_recheck_reason ? ` · ${o.codes_recheck_reason}` : ""}`}
          >
            ↩ {t("Recheck codes")} #{o.recheck_attempt || 1}{o.codes_recheck_reason ? ` · ${o.codes_recheck_reason}` : ""}
          </div>
        )}
        {o.status === "production" && o.transfer_sent_back_at && (
          <div
            className="mt-1 text-[11px] font-sans font-semibold text-rose-700 whitespace-nowrap truncate max-w-[220px] xl:max-w-[300px] 2xl:max-w-[560px]"
            data-testid={`transfer-sent-back-line-${o.id}`}
            title={`${t("Sent back from Product Transfer")} · ${o.transfer_sent_back_by || ""} · ${(o.transfer_sent_back_at || "").slice(0, 10)}${o.transfer_sent_back_reason ? ` · ${o.transfer_sent_back_reason}` : ""}`}
          >
            ↩ {t("Sent back from Product Transfer")}{o.transfer_sent_back_reason ? ` · ${o.transfer_sent_back_reason}` : ""}
          </div>
        )}
      </td>
      {isBackOffice && <td className="px-4 py-3">{o.customer?.name || "—"}</td>}
      {isBackOffice && stage !== "pre_order" && (
        <td className="px-4 py-3" data-testid={`factory-cell-${o.id}`}>
          {(() => {
            // On Pre Orders we hide the factory badge until the order has been
            // added to an F1 or F2 HQ Check workbook — before that the factory
            // assignment is meaningless to the user (it's just a placeholder).
            const hqCreated = !!(o.hq_check_sent || o.hq_checked || o.manual_production);
            if (stage === "pre_order" && !hqCreated) {
              return <span className="text-slate-300">—</span>;
            }
            // Prefer the factory the HQ check/order was actually created for,
            // fall back to `assigned_factory` for stages further downstream.
            const raw = (o.hq_check_factory || o.manual_production_factory || o.assigned_factory || "factory_1").toLowerCase();
            const f = raw.startsWith("f") && !raw.startsWith("factory_")
              ? `factory_${raw.replace("f", "")}` : raw;
            const meta = {
              factory_1: { label: "F1", tone: "bg-red-50 text-red-700 border-red-200" },
              factory_2: { label: "F2", tone: "bg-blue-50 text-blue-700 border-blue-200" },
              factory_3: { label: "F3", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
            }[f] || { label: f, tone: "bg-slate-50 text-slate-700 border-slate-200" };
            return (
              <Badge className={`border font-mono text-[11px] ${meta.tone}`} title={f}>{meta.label}</Badge>
            );
          })()}
        </td>
      )}
      <td className="px-4 py-3 text-slate-700">{[o.brand, o.model, o.year].filter(Boolean).join(" · ") || <span className="text-slate-400">awaiting decode</span>}</td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-2" data-testid={`order-color-${o.id}`}>
          {(() => {
            const ec = effectiveColor(o);
            return (
              <>
                <ColorDot color={ec.color} testid={`color-dot-${o.id}`} />
                {ec.color || <span className="text-slate-400">—</span>}
                <CustomColorNote ec={ec} testid={`custom-color-note-${o.id}`} />
              </>
            );
          })()}
        </span>
      </td>
      {batchView && (
        <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`batch-cell-${o.id}`}>
          <div className="font-mono font-semibold text-indigo-900">📦 #{o.stock_unit?.batch_no ?? "—"}</div>
          <div className="text-slate-600">{WAREHOUSE_FLAG[o.stock_unit?.dest] || ""} {WAREHOUSE_LABEL[o.stock_unit?.dest] || o.stock_unit?.dest || "—"}</div>
        </td>
      )}
      {(stage === "packing" || stage === "inventory_packing" || (stage === "pending" && isBackOffice) || batchView) && (
        <td className="px-4 py-3 font-mono font-semibold text-slate-900" data-testid={`cat-cell-${o.id}`}>
          {o.catalog_number || <span className="text-slate-400 font-normal">—</span>}
        </td>
      )}
      {stage !== "pre_order" && (
        <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap" data-testid={`codes-${o.id}`}>
          {o.front_cover_code ? (
            <div className="leading-tight">
              <div data-testid={`codes-front-${o.id}`}><span className="text-slate-400 mr-1">F</span>{displayCode(o.front_cover_code, "front", lang)}{batchView && o.f_clamp_code && <span className="ml-2 text-slate-500" data-testid={`codes-fclamp-${o.id}`}>⟟ {o.f_clamp_code}</span>}</div>
              <div data-testid={`codes-rear-${o.id}`}><span className="text-slate-400 mr-1">R</span>{o.rear_cover_code ? displayCode(o.rear_cover_code, "rear", lang) : "—"}{batchView && o.r_clamp_code && <span className="ml-2 text-slate-500" data-testid={`codes-rclamp-${o.id}`}>⟟ {o.r_clamp_code}</span>}</div>
              {batchView && (o.spacer || o.spacer_qty || o.rim_size) && (
                <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`codes-spec-${o.id}`}>
                  {o.spacer ? `spacer ${o.spacer}` : "no spacer"}{o.spacer_qty ? ` ×${o.spacer_qty}` : ""}{o.rim_size ? ` · rim ${o.rim_size}"` : ""}
                </div>
              )}
            </div>
          ) : (batchView ? <span className="text-amber-700" data-testid={`codes-missing-${o.id}`}>⚠ {t("no codes in catalog yet")}</span> : "—")}
        </td>
      )}
      {stage === "inventory_packing" && (
        <td className="px-4 py-3"><LogoBadge order={o} size={28} testid={`inv-packing-logo-${o.id}`} /></td>
      )}
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
        {(() => {
          // ⏳ Waiting for customer reply (Missing Info / Photo Reminder /
          // Lead → Order email sent) — in EVERY tab the stage badge itself
          // becomes the purple WAITING FOR REPLY badge. The order stays in
          // its tab; the badge reverts when the reply/photos arrive.
          if (o.waiting_reply && !(o.cancelled === true || o.status === "cancelled")) {
            return (
              <Badge
                className="bg-violet-600 text-white border border-violet-700 font-bold text-sm px-3.5 py-1.5 shadow-[0_0_12px_rgba(139,92,246,0.55)]"
                data-testid={`waiting-reply-badge-${o.id}`}
                title={`${t(STAGE_LABEL[o.status] || o.status)} · waiting for customer reply · ${o.waiting_reply_channel || "email"} · since ${(o.waiting_reply_at || "").slice(0, 16).replace("T", " ")}`}
              >
                ⏳ {t("WAITING FOR REPLY")}
              </Badge>
            );
          }
          // 📷 Bad photos — Pending reviewer sent the order back to Pre Orders.
          if (o.bad_photos && o.status === "pre_order" && o.cancelled !== true) {
            return (
              <Badge
                className="bg-rose-500 text-white border border-rose-600 font-bold text-sm px-3.5 py-1.5 shadow-[0_0_12px_rgba(244,63,94,0.5)]"
                data-testid={`bad-photos-badge-${o.id}`}
                title={`${o.bad_photos_note || ""}${o.bad_photos_note_translation ? " / " + o.bad_photos_note_translation : ""}${o.bad_photos_by ? " · " + o.bad_photos_by : ""}${o.bad_photos_at ? " · " + o.bad_photos_at.slice(0, 16).replace("T", " ") : ""}`}
              >
                📷 {t("BAD PHOTOS")}
              </Badge>
            );
          }
          if (stage === "packing" && isPackingUser) {
            if (o.packed_at) {
              return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" data-testid={`stage-${o.id}`}>{t("Complete Packing")}</Badge>;
            }
            return <Badge className="bg-amber-50 text-amber-700 border-amber-200 border" data-testid={`stage-${o.id}`}>{t("Packing Ready")}</Badge>;
          }
          if (stage === "completed" && (user.role === "factory_1" || user.role === "factory_2" || user.role === "factory_3")) {
            return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" data-testid={`stage-${o.id}`}>{t("Order Received")}</Badge>;
          }
          if (o.status === "production") {
            const sub = productionSubstage(o);
            return <Badge className={PRODUCTION_SUBSTAGE_BADGE[sub]} data-testid={`sub-status-${o.id}`}>{t(PRODUCTION_SUBSTAGE[sub])}</Badge>;
          }
          // Skip the redundant "Pre Orders" pill on the Pre Order list — the
          // page header already says "Pre Orders", so the row only needs the
          // conditional NOT READY / PHOTOS RECEIVED / WAITING REPLY pills.
          if (o.status === "pre_order") return null;
          // Pending tab — 3-stage HQ Check workflow indicator (Feb 28, 2026 rule):
          //   STAGE 1  no HQ Check yet     → "PENDING ORDERS" (BIG blue)
          //   STAGE 2  hq_check_sent=True   → "PENDING CHECK" (amber, main size)
          //   STAGE 3  hq_checked=True      → "CHECK COMPLETED" (emerald, main size)
          // These stages replace the redundant photos-received/waiting-reply badges
          // on this tab per user rule "no label photo received in this stage".
          if (stage === "pending") {
            if (o.hq_checked) {
              return (
                <Badge className="bg-emerald-500 text-white border border-emerald-600 font-bold px-3 py-1"
                       data-testid={`stage-check-completed-${o.id}`}
                       title={`HQ Check codes synced from Drive · by ${o.hq_approved_by || o.hq_checked_by || "—"} · tab ${o.hq_check_tab_uploaded || o.hq_check_tab_name || "—"}`}>
                  ✅ {t("CHECK COMPLETED")}
                </Badge>
              );
            }
            if (o.hq_check_sent) {
              return (
                <Badge className="bg-amber-500 text-white border border-amber-600 font-bold px-3 py-1"
                       data-testid={`stage-pending-check-${o.id}`}
                       title={`HQ Check created · ${(o.hq_check_factory || "").toUpperCase()} · tab ${o.hq_check_tab_name || "—"} · signed by ${o.hq_check_sent_by || "—"}`}>
                  🕒 {t("PENDING CHECK")}
                </Badge>
              );
            }
            return (
              <Badge className="bg-blue-600 text-white border border-blue-700 font-bold text-sm px-3.5 py-1.5"
                     data-testid={`stage-pending-orders-${o.id}`}>
                {t("PENDING ORDERS")}
              </Badge>
            );
          }
          // Advance tab — Stage 1 workflow indicator (Feb 28, 2026 rule):
          //   STAGE 1  order lives at status=advance → "CODES RECEIVED" (emerald, big)
          //   (future stages, if any, will be added here beside this one.)
          if (stage === "advance") {
            return (
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200"
                     data-testid={`stage-codes-received-${o.id}`}
                     title={o.front_cover_code ? `Codes: ${o.front_cover_code} / ${o.rear_cover_code}` : "Codes received — this order is ready for production."}>
                ✅ {t("CODES RECEIVED")}
              </Badge>
            );
          }
          // Inventory Packing — once inventory_packed_at is stamped, the row is
          // "READY TO BE SHIPPED" (green). The packer may ship it later from
          // this same list via the green Ship button in the actions column.
          if (stage === "inventory_packing" && o.inventory_packed_at) {
            return (
              <Badge className="bg-emerald-500 text-white border-emerald-600 border font-semibold" data-testid={`stage-${o.id}`}>
                ✅ {o.is_pickup ? t("READY FOR PICK UP") : t("READY TO BE SHIPPED")}
              </Badge>
            );
          }
          // Fulfilled tab — pick-up orders are "READY FOR PICK UP" (final state).
          if (o.status === "fulfilled" && o.fulfilled_method === "pickup_ready") {
            return (
              <Badge className="bg-violet-600 text-white border-violet-700 border font-semibold" data-testid={`stage-${o.id}`}>
                🏬 {t("READY FOR PICK UP")}
              </Badge>
            );
          }
          return <Badge className={BADGE_CLS[STAGE_BADGE[o.status]] + " border"}>{t(STAGE_LABEL[o.status] || o.status)}</Badge>;
        })()}
        {o.shipping_tracking_number && (o.status === "fulfilled" || o.status === "shipping") && (
          <TrackingLink number={o.shipping_tracking_number} carrier={o.shipping_carrier} testid={`row-tracking-${o.id}`}
                        className="font-mono text-[11px] text-slate-600 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50">
            📦 {o.shipping_tracking_number}
          </TrackingLink>
        )}
        {o.status === "pre_order" && !o.waiting_reply && !(o.vin || "").trim() && (
          <Badge className="bg-red-50 text-red-700 border border-red-200" data-testid={`not-ready-${o.id}`}>{t("NOT READY")}</Badge>
        )}
        {/* Pending tab suppresses photos-received/waiting-reply — its stage
            badge (above) already covers the HQ Check workflow. */}
        {stage !== "pending" && (
          /* ── All other stages keep the ORIGINAL two-badge behavior ── */
          <>
            {o.status === "lead" && (
              <Badge
                className="bg-emerald-100 text-emerald-800 border border-emerald-300"
                data-testid={`lead-badge-${o.id}`}
                title="Prospective order — no payment yet. Requires Back Office Administrator approval."
              >
                🌱 LEAD
              </Badge>
            )}
            {o.manual_production ? (
              <Badge
                className="bg-orange-50 text-orange-800 border border-orange-300 font-semibold text-[9px] px-1.5 py-[1px] leading-none"
                data-testid={`manual-production-${o.id}`}
                title={`Manual Production · exported to ${o.manual_production_factory?.toUpperCase() || "?"} · tab ${o.hq_order_tab_name || "—"} · signed by ${o.manual_production_by || "—"}`}
              >
                🏭 MANUAL PRODUCTION
              </Badge>
            ) : o.hq_checked ? (
              <Badge
                className="bg-indigo-50 text-indigo-800 border border-indigo-300 font-semibold text-[9px] px-1.5 py-[1px] leading-none"
                data-testid={`manual-upload-${o.id}`}
                title={`Codes ingested from HQ Check workbook · approved by ${o.hq_approved_by || o.hq_checked_by || "—"} · tab ${o.hq_check_tab_uploaded || "—"}`}
              >
                📤 {t("MANUAL UPLOAD")}
              </Badge>
            ) : (o.photos_received && stage === "pre_order" && !(user?.role || "").startsWith("factory_") && (
              <Link to={`/order/${o.id}`} state={{ from: stage }} className="inline-flex">
                <Badge
                  className="bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-pointer hover:bg-emerald-100"
                  data-testid={`photos-received-${o.id}`}
                  title={o.drive_folder_name ? `Open order · Drive folder: ${o.drive_folder_name}` : "Open order"}
                >
                  📸 {t("PHOTOS RECEIVED")}
                </Badge>
              </Link>
            ))}
          </>
        )}
        {o.cancel_pending && <CancelPendingBadge o={o} user={user} />}
        {(o.cancelled === true || o.status === "cancelled") && (
          <Badge
            className="bg-red-100 text-red-800 border border-red-300"
            data-testid={`refunded-${o.id}`}
            title={o.cancel_reason || "Order was cancelled — refund flow"}
          >
            ✕ REFUNDED
          </Badge>
        )}
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        {stage === "product_transfer" && isPackingUser ? (
          // While the order is still in product_transfer, always expose the
          // Mark Received action. A stale packing_received_at from a prior
          // roll-back must not strand the row without an action button —
          // clicking Mark Received re-stamps the receiver and pushes the
          // order forward to `packing` idempotently.
          <div className="inline-flex items-center gap-2">
            <TransferSendBackButton order={o} />
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => onMarkReceived.mutate(o.id)}
              disabled={onMarkReceived.isPending && onMarkReceived.variables === o.id}
              title={`Will be recorded as: ${mySignature}`}
              data-testid={`received-btn-${o.id}`}
            >
              {onMarkReceived.isPending && onMarkReceived.variables === o.id
                ? t("Marking...")
                : t("✓ Mark Received")}
            </Button>
          </div>
        ) : (stage === "packing" || stage === "inventory_packing") && isPackingUser && !(stage === "inventory_packing" ? o.inventory_packed_at : o.packed_at) ? (
          // Packing Orders / Inventory Packing → prominent "▶ Start Packing"
          // CTA that opens the Packing Checklist tab directly (no extra click).
          // Inv Packing uses ORANGE so it's visually distinct from Packing
          // Orders (green) and matches the pre-completion "in progress" hue.
          <Link
            to={`/order/${o.id}?tab=packing`}
            state={{ from: stage }}
            data-testid={`start-packing-${o.id}`}
            className={
              "inline-flex items-center justify-center rounded-md font-semibold px-5 py-2.5 text-sm shadow-sm hover:shadow text-white " +
              (stage === "inventory_packing" && !pickupPending(o)
                ? "bg-orange-500 hover:bg-orange-600"
                : pickupPending(o)
                ? "bg-amber-700 hover:bg-amber-800"
                : "bg-emerald-600 hover:bg-emerald-700")
            }
          >
            {pickupPending(o) ? `📦 ${t("PICK UP THE PART")}` : `▶ ${t("START PACKING")}`}
          </Link>
        ) : stage === "inventory_packing" && isPackingUser && o.inventory_packed_at ? (
          // Inv Packing done — order is ready. Shipping orders get the SAME three
          // shipping actions as the other stations (ShipStation / Shopify label /
          // Self Checkout) — any of them closes the order as Order Fulfilled
          // (user rule Oct 6, 2026; replaces the old "🚚 SHIP → Shipping" button).
          // Pick-up orders get 🏬 PICK UP READY instead (emails the client →
          // Order Fulfilled) — a pick-up order is never shipped.
          <div className="flex items-center gap-2 flex-wrap justify-end" data-testid={`ready-actions-${o.id}`}>
            {o.is_pickup && pickupReady ? (
              <Button
                size="sm"
                className="bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2.5 shadow-sm hover:shadow"
                data-testid={`pickup-ready-btn-${o.id}`}
                onClick={() => pickupReady.mutate(o.id)}
                disabled={pickupReady.isPending && pickupReady.variables === o.id}
                title="Email the client that the order is waiting for pick-up and move it to Order Fulfilled"
              >
                {pickupReady.isPending && pickupReady.variables === o.id ? t("Sending…") : t("🏬 PICK UP READY")}
              </Button>
            ) : (
              <ReceivedActions o={o} hideDivert />
            )}
          </div>
        ) : stage === "product_transfer" && isFactory ? (
          <span className="text-xs font-mono text-slate-500">{t("status only")}</span>
        ) : prodActions && stage === "production" ? (() => {
          // Factory One production row: the sign / print / sign-off actions live
          // right here (same as the order-page panels). Row click still opens the order.
          const sub = productionSubstage(o);
          const dl = productionDeadline(o);
          const overdue = dl && dl.getTime() < Date.now() && sub !== "signed_off";
          const req = o.production_extension_request;
          return (
            <div className="flex flex-col items-end gap-1.5" data-testid={`prod-actions-${o.id}`}>
              {qcHold && <QcResolveButton orderId={o.id} />}
              {sub === "awaiting" && (
                <>
                  <Button
                    size="sm"
                    data-testid={`prod-sign-${o.id}`}
                    disabled={prodActions.busy}
                    onClick={() => prodActions.sign(o.id)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 shadow-sm"
                    title={`${t("Signed as")}: ${mySignature}`}
                  >
                    ✍ {t("Sign & Start Production")}
                  </Button>
                  {!o.claim_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`prod-return-${o.id}`}
                    disabled={prodActions.busy}
                    onClick={() => prodActions.returnAdvance(o.id)}
                    className="border-amber-500 text-amber-700 hover:bg-amber-50 font-semibold"
                  >
                    ↩ {t("Return")} {t("Advance Orders")}
                  </Button>
                  )}
                </>
              )}
              {sub === "to_print" && (
                <Button
                  size="sm"
                  data-testid={`prod-print-${o.id}`}
                  disabled={prodActions.busy}
                  onClick={() => prodActions.print(o)}
                  className="bg-violet-600 hover:bg-violet-700 text-white font-semibold px-4 shadow-sm"
                >
                  🖨 {t("Print")}
                </Button>
              )}
              {sub === "in_production" && (
                <>
                  <Button
                    size="sm"
                    data-testid={`prod-complete-${o.id}`}
                    disabled={prodActions.busy || qcHold}
                    onClick={() => prodActions.complete(o.id)}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 shadow-sm"
                    title={qcHold ? t("Resolve the Quality Control issue before completing production.") : `${t("Signed as")}: ${mySignature}`}
                  >
                    ✓ {t("Sign Off & Transfer")}
                  </Button>
                  <ExtensionRequestButton order={o} />
                  {dl && (
                    <span className={`text-[11px] font-mono ${overdue ? "text-red-700 font-bold" : "text-slate-500"}`} data-testid={`prod-deadline-${o.id}`}>
                      ⏳ {t("Due")} {dl.toISOString().slice(0, 10)}{Number(o.production_extension_days) > 0 ? ` (+${o.production_extension_days} ${t("days")})` : ""}
                    </span>
                  )}
                  {req?.status === "approved" && Number(o.production_extension_days) > 0 && (
                    <span className="text-[10px] font-mono text-emerald-700" data-testid={`prod-ext-approved-${o.id}`}>✓ {t("Approved")} +{req.days} {t("days")}</span>
                  )}
                </>
              )}
            </div>
          );
        })() : (
          <div className="flex items-center justify-end gap-3">
            {stage === "pre_order" && !o.photos_received && isBackOffice && (
              <PreorderPhotoUploadButton order={o} />
            )}
            {(stage === "no_mold" || stage === "no-mold") && isBackOffice && (
              <Button
                size="sm"
                onClick={() => reactivate.mutate()}
                disabled={reactivate.isPending}
                data-testid={`reactivate-${o.id}`}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1.5 shadow-sm"
                title="Move this order back to Advance Orders and clear the No Mold flag"
              >
                {reactivate.isPending ? t("Reactivating…") : t("🔄 Reactivate")}
              </Button>
            )}
            {(stage === "packing" || stage === "inventory_packing") && isPackingUser && (stage === "inventory_packing" ? o.inventory_packed_at : o.packed_at) && o.shipping_eta && (
              <span
                className="text-[11px] font-mono text-slate-600 whitespace-nowrap"
                data-testid={`shipping-eta-${o.id}`}
                title={`ETA calculated 7 days from ${(stage === "inventory_packing" ? "inventory_packed_at" : "packed_at")}`}
              >
                🚚 Ships by {o.shipping_eta.slice(0, 10)}
              </span>
            )}
            <Link
              to={`/order/${o.id}`}
              state={{ from: stage }}
              data-testid={`order-open-${o.id}`}
              className="text-sm underline text-slate-700 hover:text-slate-900"
            >Open →</Link>
          </div>
        )}
      </td>
    </tr>
  );
}


// Cancelled row inside Orders Received view. Super Admin gets a red
// "Delete Permanently" button that opens a typed-PO confirmation dialog.
// Other users see only "Open →".
function CancelledRow({ o, stage, isSuperAdmin }) {
  const [delOpen, setDelOpen] = React.useState(false);
  return (
    <tr className="border-t border-red-100" data-testid={`cancelled-row-${o.id}`}>
      <td className="px-4 py-2 font-mono font-semibold text-red-900">
        <div className="flex items-center gap-2 flex-wrap">
          <span>{o.po_number}</span>
          <UsFlag order={o} testid={`us-flag-cancelled-${o.id}`} />
          <Badge className="bg-red-100 text-red-800 border border-red-300 font-semibold" title={o.cancel_reason || "Cancelled"}>✕ REFUNDED</Badge>
        </div>
      </td>
      <td className="px-4 py-2 text-red-900/70">{[o.brand, o.model, o.year].filter(Boolean).join(" · ") || "—"}</td>
      <td className="px-4 py-2 text-red-900/70 text-xs">{o.cancel_reason || "—"}</td>
      <td className="px-4 py-2 font-mono text-red-900/70">{(o.cancelled_at || o.received_at || "").slice(0, 10) || "—"}</td>
      <td className="px-4 py-2 text-right">
        <div className="inline-flex items-center gap-2">
          {isSuperAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDelOpen(true)}
              data-testid={`cancelled-delete-${o.id}`}
              className="border-red-600 text-red-700 hover:bg-red-100"
            >
              🗑 Delete Permanently
            </Button>
          )}
          <Link
            to={`/order/${o.id}`}
            state={{ from: stage }}
            className="text-sm underline text-red-800 hover:text-red-900"
          >Open →</Link>
        </div>
        {delOpen && (
          <span onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
            <DeletePermanentlyDialog order={o} open={delOpen} onOpenChange={setDelOpen} />
          </span>
        )}
      </td>
    </tr>
  );
}
